import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { PointManagementService } from '../services/pointManagementService';
import { userDataService } from '../services/userDataService';
import { ItemCardService } from '../services/itemCardService';
import { CoinManagementService } from '../services/coinManagementService';
import { StudentAssignmentHistory } from '../types/assignmentPointsMemo';
import { PointRecord } from '../types/pointManagement';
import { CoinHistory, ClassMember } from '../types/alarm';
import GemIcon from './icons/GemIcon';
import { User } from '../types/alarm';
import StoreModal from './StoreModal';
import { withImageVersion } from '../utils/imageVersion';

// 타입별 필터 컨테이너 - 이전 형태 (슬라이더)
const ItemCardTypeFilterContainer = styled.div`
  display: flex;
  background: var(--color-border);
  border-radius: 12px;
  padding: 4px;
  position: relative;
  width: 100%;
  margin-bottom: 16px;
`;

// 타입별 필터 슬라이더
const ItemCardTypeFilterSlider = styled.div<{ $position: number }>`
  position: absolute;
  top: 4px;
  left: ${props => `${props.$position * 33.333}%`};
  width: calc(33.333% - 8px);
  height: calc(100% - 8px);
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: left 0.3s ease;
  z-index: 1;
  margin: 0 4px;
`;

// 타입별 필터 버튼
const ItemCardTypeFilterButton = styled.button<{ $isActive: boolean }>`
  position: relative;
  z-index: 2;
  background: transparent;
  border: none;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s ease;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    color: var(--color-text-primary);
  }
  
  @media (max-width: 600px) {
    flex-direction: column;
    justify-content: center;
    white-space: normal;
    padding: 8px 16px;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 타입별 필터 버튼 왼쪽
const ItemCardTypeFilterButtonLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  
  .full-text {
    display: inline !important;
    @media (max-width: 480px) {
      display: none !important;
    }
  }
  
  .short-text {
    display: none !important;
    @media (max-width: 480px) {
      display: inline !important;
    }
  }
`;

// 타입별 필터 버튼 오른쪽
const ItemCardTypeFilterButtonRight = styled.div<{ $isActive: boolean }>`
  font-weight: 700;
  color: var(--color-text-primary);
  white-space: nowrap;
  font-size: 12px;
  flex-shrink: 0;
  
  @media (max-width: 600px) {
    font-size: 11px;
  }
`;

interface StudentPointDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: ClassMember | null;
  userId: string;
  classId: string;
  memberGroup?: string;
  coinDefaultPoints?: number; // 코인 기본 단위
  onDataChange?: () => void; // 데이터 변경 시 부모에게 알림
  user: User; // 사용자 정보 (라이센스 체크용)
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
}

const StudentPointDetailModal: React.FC<StudentPointDetailModalProps> = ({
  isOpen,
  onClose,
  student,
  userId,
  classId,
  memberGroup,
  coinDefaultPoints = 10, // 기본값 10코인
  onDataChange,
  user,
  onLicenseClick
}) => {
  // 메인 탭 상태 (포인트/코인/아이템카드)
  const [mainTab, setMainTab] = useState<'points' | 'coins' | 'itemcards'>('itemcards');
  
  // 포인트 탭 내부 상태
  const [activeTab, setActiveTab] = useState<'assignment' | 'positive' | 'negative' | null>('assignment');
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'general' | 'project' | 'routine'>('all');
  const [positiveSearchText, setPositiveSearchText] = useState<string>('');
  const [negativeSearchText, setNegativeSearchText] = useState<string>('');
  const [positiveSearchFilter, setPositiveSearchFilter] = useState<string>('');
  const [negativeSearchFilter, setNegativeSearchFilter] = useState<string>('');
  const [rewardPoints, setRewardPoints] = useState<PointRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [deletedPointIds, setDeletedPointIds] = useState<string[]>([]);

  // 코인 히스토리에서 총 코인 수 계산
  const calculateTotalCoins = (coinsHistory?: any[]): number => {
    if (!coinsHistory || coinsHistory.length === 0) return 0;
    return coinsHistory.reduce((total, history) => total + (history.earnedCoin || 0), 0);
  };

  // earnedAt을 Date로 안전하게 변환
  const getEarnedAtDate = (earnedAt: any): Date => {
    if (earnedAt && typeof earnedAt === 'object' && 'toDate' in earnedAt && typeof (earnedAt as any).toDate === 'function') {
      return (earnedAt as any).toDate();
    }
    if (earnedAt instanceof Date) {
      return earnedAt;
    }
    const parsed = earnedAt ? new Date(earnedAt) : new Date();
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  };

  // 코인 히스토리를 위한 고유 ID (신규 id 필드 우선 사용)
  const getCoinHistoryId = (history: CoinHistory): string => {
    const historyIdFromData = (history as any)?.id as string | undefined;
    const earnedAtDate = getEarnedAtDate((history as any)?.earnedAt);
    return historyIdFromData || `${earnedAtDate.getTime()}_${history.earnedCoin}_${history.earnedCoinMemo || ''}`;
  };

  // 코인 탭 상태
  const [coinSearchText, setCoinSearchText] = useState<string>('');
  const [coinSearchFilter, setCoinSearchFilter] = useState<string>('');
  const [coinHistory, setCoinHistory] = useState<CoinHistory[]>([]);
  const [currentCoins, setCurrentCoins] = useState<number>(0);
  
  // 코인 추가/사용 모달 상태
  const [showCoinAddModal, setShowCoinAddModal] = useState<boolean>(false);
  const [showCoinUseModal, setShowCoinUseModal] = useState<boolean>(false);
  const [coinAmount, setCoinAmount] = useState<number>(coinDefaultPoints);
  const [coinMemo, setCoinMemo] = useState<string>('');

  // 스토어 모달 상태
  const [showStoreModal, setShowStoreModal] = useState<boolean>(false);
  const lastLoadedStudentIdRef = useRef<string | null>(null);
  const lastLoadKeyRef = useRef<string | null>(null);
  const lastLoadAtRef = useRef<number>(0);
  const loadInFlightRef = useRef<Promise<void> | null>(null);
  
  // 아이템 카드 사용 토스트 상태
  const [useCardToast, setUseCardToast] = useState<{ show: boolean; itemName: string; cardImage: string; remainingCount: number }>({
    show: false,
    itemName: '',
    cardImage: 'default',
    remainingCount: 0
  });
  const useCardToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // 액티브 카드 사용 모달 상태
  const [showUseCardModal, setShowUseCardModal] = useState<boolean>(false);
  const [selectedCardForUse, setSelectedCardForUse] = useState<any>(null);
  const [useCardMemo, setUseCardMemo] = useState<string>('');
  const [isUsingCard, setIsUsingCard] = useState<boolean>(false);
  
  // 아이템 카드 삭제 모달 상태
  const [showDeleteCardModal, setShowDeleteCardModal] = useState<boolean>(false);
  const [selectedCardForDelete, setSelectedCardForDelete] = useState<any>(null);
  const [isDeletingCard, setIsDeletingCard] = useState<boolean>(false);
  const [isEditingCoinAmount, setIsEditingCoinAmount] = useState<boolean>(false);
  const [tempCoinInput, setTempCoinInput] = useState<string>('');
  
  // 코인 히스토리 삭제 상태
  const [editingCoinRowId, setEditingCoinRowId] = useState<string | null>(null);
  const [deletedCoinIds, setDeletedCoinIds] = useState<string[]>([]);
  const [showCoinDeleteConfirmModal, setShowCoinDeleteConfirmModal] = useState(false);
  const [isDeletingCoins, setIsDeletingCoins] = useState(false);
  
  // 과제 포인트 상태
  const [assignmentData, setAssignmentData] = useState<StudentAssignmentHistory | null>(null);
  
  // 아이템 카드 상태
  const [studentItemCards, setStudentItemCards] = useState<any[]>([]);
  const [itemCardDetails, setItemCardDetails] = useState<{[key: string]: any}>({});
  const [itemCardFilter, setItemCardFilter] = useState<'all' | 'passive' | 'active'>('all');
  const [itemCardCategoryFilter, setItemCardCategoryFilter] = useState<'all' | 'levelup' | 'assignment' | 'store'>('all');

  // 페이지네이션 상태
  const [assignmentPage, setAssignmentPage] = useState(1);
  const [positivePage, setPositivePage] = useState(1);
  const [negativePage, setNegativePage] = useState(1);
  const [coinPage, setCoinPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // loadRewardPoints 함수는 loadAllData에 통합됨

  // 코인 증가 함수
  const handleIncrementCoin = () => {
    setCoinAmount(prev => prev + coinDefaultPoints);
  };

  // 코인 감소 함수
  const handleDecrementCoin = () => {
    setCoinAmount(prev => Math.max(0, prev - coinDefaultPoints));
  };

  // 코인 숫자 클릭 시 편집 모드
  const handleCoinAmountClick = () => {
    setIsEditingCoinAmount(true);
    setTempCoinInput(String(coinAmount));
  };

  // 코인 입력 변경
  const handleCoinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 코인 사용 모달에서는 - 접두사를 제거하고 숫자만 허용
    const numericValue = value.startsWith('-') ? value.slice(1) : value;
    if (numericValue === '' || /^\d+$/.test(numericValue)) {
      setTempCoinInput(numericValue);
    }
  };

  // 코인 입력 완료
  const handleCoinInputBlur = () => {
    const value = parseInt(tempCoinInput) || 0;
    setCoinAmount(Math.max(0, value));
    setIsEditingCoinAmount(false);
  };

  // 코인 입력 Enter 키
  const handleCoinInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCoinInputBlur();
    }
  };

  // 코인 추가 함수
  const handleCoinAdd = async () => {
    if (!student || !userId || !classId || coinAmount <= 0 || !coinMemo.trim()) return;

    try {
      console.log('🪙 코인 추가 시작:', { studentId: student.id, amount: coinAmount, memo: coinMemo });
      
      // ClassMembersCoin에 저장
      const coinManagementService = new CoinManagementService(userId, classId, memberGroup);
      await coinManagementService.grantCoinsToStudent(student.id, coinAmount, coinMemo, 'General');

      // 업데이트된 데이터 다시 로드
      const updatedCoinData = await coinManagementService.getStudentCoinData(student.id);
      
      if (updatedCoinData) {
        setCurrentCoins(updatedCoinData.coins || 0);
        setCoinHistory(updatedCoinData.coinsHistory || []);
      }

      console.log('✅ 코인 추가 완료 (ClassMembersCoin)');

      // 모달 닫기 및 입력 필드 초기화
      setShowCoinAddModal(false);
      setCoinAmount(coinDefaultPoints);
      setCoinMemo('');
      setIsEditingCoinAmount(false);
      
      // 부모 컴포넌트에 데이터 변경 알림
      onDataChange?.();

    } catch (error) {
      console.error('코인 추가 실패:', error);
    }
  };

  // 코인 사용 함수
  const handleCoinUse = async () => {
    if (!student || !userId || !classId || coinAmount <= 0 || !coinMemo.trim()) return;

    try {
      console.log('🪙 코인 사용 시작:', { studentId: student.id, amount: coinAmount, memo: coinMemo });
      
      // ClassMembersCoin에 저장 (음수로 저장)
      const coinManagementService = new CoinManagementService(userId, classId, memberGroup);
      await coinManagementService.grantCoinsToStudent(student.id, -coinAmount, coinMemo, 'General');

      // 업데이트된 데이터 다시 로드
      const updatedCoinData = await coinManagementService.getStudentCoinData(student.id);
      
      if (updatedCoinData) {
        setCurrentCoins(updatedCoinData.coins || 0);
        setCoinHistory(updatedCoinData.coinsHistory || []);
      }

      console.log('✅ 코인 사용 완료 (ClassMembersCoin)');

      // 모달 닫기 및 입력 필드 초기화
      setShowCoinUseModal(false);
      setCoinAmount(coinDefaultPoints);
      setCoinMemo('');
      setIsEditingCoinAmount(false);
      
      // 부모 컴포넌트에 데이터 변경 알림
      onDataChange?.();

    } catch (error) {
      console.error('코인 사용 실패:', error);
    }
  };

  // 스토어 모달 열기
  const handleOpenStoreModal = () => {
    setShowStoreModal(true);
  };

  // 아이템 카드 삭제 버튼 클릭 핸들러
  const handleDeleteCardClick = (e: React.MouseEvent, itemCard: any) => {
    e.stopPropagation(); // 카드 클릭 이벤트 전파 방지
    setSelectedCardForDelete(itemCard);
    setShowDeleteCardModal(true);
  };

  // 아이템 카드 삭제 처리
  const handleDeleteCard = async () => {
    if (!student || !userId || !classId || !selectedCardForDelete) return;
    
    setIsDeletingCard(true);
    
    try {
      const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);
      await partitionService.updateStudentItemCardsAtomic(student.id, (currentCards) =>
        currentCards.filter((card: any) => card.instanceId !== selectedCardForDelete.instanceId)
      );

      // 로컬 상태 업데이트
      setStudentItemCards(prev => prev.filter(c => c.instanceId !== selectedCardForDelete.instanceId));
      
      console.log('✅ 아이템 카드 삭제 완료:', itemCardDetails[selectedCardForDelete.itemCardId]?.name);
      
      // 모달 닫기
      setShowDeleteCardModal(false);
      setSelectedCardForDelete(null);
      
      // 부모 컴포넌트에 데이터 변경 알림
      onDataChange?.();
      
    } catch (error: any) {
      console.error('아이템 카드 삭제 실패:', error);
      alert(`삭제에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
    } finally {
      setIsDeletingCard(false);
    }
  };

  // 액티브 카드 클릭 핸들러
  const handleActiveCardClick = (itemCard: any) => {
    if (itemCard.type !== 'active') return;
    setSelectedCardForUse(itemCard);
    // 현재 날짜를 "mm.dd 사용" 형식으로 기본값 설정
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    setUseCardMemo(`${month}.${day} 사용`);
    setShowUseCardModal(true);
  };

  // 액티브 카드 사용 처리
  const handleUseActiveCard = async () => {
    if (!student || !userId || !classId || !selectedCardForUse) return;
    
    setIsUsingCard(true);
    
    try {
      const now = new Date();
      const usageRecord: any = { usedAt: now };
      if (useCardMemo.trim()) {
        usageRecord.memo = useCardMemo.trim();
      }

      const { ClassMembersPartitionService } = await import('../services/classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(userId, classId, memberGroup);

      let currentCount = 0;
      await partitionService.updateStudentItemCardsAtomic(student.id, (currentCards) => {
        const nextCards = [...currentCards];
        const cardIndex = nextCards.findIndex(
        (card: any) => card.instanceId === selectedCardForUse.instanceId
      );
      if (cardIndex === -1) {
        throw new Error('아이템 카드를 찾을 수 없습니다.');
      }
      
        currentCount = nextCards[cardIndex].remainingUseCount || 0;
      if (currentCount <= 0) {
        throw new Error('사용 가능한 횟수가 없습니다.');
      }
      
        nextCards[cardIndex].remainingUseCount = currentCount - 1;
        if (!nextCards[cardIndex].usageHistory) {
          nextCards[cardIndex].usageHistory = [];
        }
        nextCards[cardIndex].usageHistory.push(usageRecord);

        return nextCards;
      });
      
      // 로컬 상태 업데이트 - 카드를 제거하지 않고 사용 횟수만 차감
      setStudentItemCards(prev => prev.map(c => 
        c.instanceId === selectedCardForUse.instanceId 
          ? { ...c, remainingUseCount: currentCount - 1, usageHistory: [...(c.usageHistory || []), usageRecord] }
          : c
      ));
      
      const usedItemName = itemCardDetails[selectedCardForUse.itemCardId]?.name || '아이템';
      const usedCardImage = itemCardDetails[selectedCardForUse.itemCardId]?.cardImage || 'default';
      const remainingCount = currentCount - 1;
      console.log('✅ 아이템 카드 사용 완료:', usedItemName, '남은 횟수:', remainingCount);
      
      // 모달 닫기
      setShowUseCardModal(false);
      setSelectedCardForUse(null);
      setUseCardMemo('');
      
      // 사용 완료 토스트 표시
      if (useCardToastTimeoutRef.current) clearTimeout(useCardToastTimeoutRef.current);
      setUseCardToast({ show: true, itemName: usedItemName, cardImage: usedCardImage, remainingCount });
      useCardToastTimeoutRef.current = setTimeout(() => {
        useCardToastTimeoutRef.current = null;
        setUseCardToast({ show: false, itemName: '', cardImage: 'default', remainingCount: 0 });
      }, 2500);
      
      // 아이템 카드 사용은 코인 데이터를 변경하지 않으므로 부모(포인트 조회 페이지) 리로드 불필요 → onDataChange 호출 제거
      
    } catch (error: any) {
      console.error('아이템 카드 사용 실패:', error);
      alert(`사용에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
    } finally {
      setIsUsingCard(false);
    }
  };

  // 코인 행 클릭 핸들러 (긍정행동 포인트 테이블과 동일한 방식)
  const handleCoinRowClick = (historyId: string) => {
    setEditingCoinRowId(editingCoinRowId === historyId ? null : historyId);
  };

  // 코인 휴지통 클릭 - 즉시 표시만 변경 (삭제 예정 토글)
  const handleDeleteCoin = (historyId: string) => {
    if (deletedCoinIds.includes(historyId)) {
      // 이미 삭제 예정이면 취소
      setDeletedCoinIds(prev => prev.filter(id => id !== historyId));
    } else {
      // 삭제 예정 목록에 추가
      setDeletedCoinIds(prev => [...prev, historyId]);
    }
  };

  // 코인 삭제 버튼 클릭 - 확인 모달 표시
  const handleCoinDeleteClick = () => {
    if (deletedCoinIds.length > 0) {
      setShowCoinDeleteConfirmModal(true);
    }
  };

  // 코인 히스토리 삭제 함수 (실제 삭제 실행)
  const handleDeleteSelectedCoinHistory = async () => {
    if (!student || !userId || !classId || deletedCoinIds.length === 0) return;

    if (isDeletingCoins) {
      console.log('⚠️ 이미 처리 중입니다.');
      return;
    }

    console.log(`🗑️ 코인 히스토리 일괄 삭제 시작: ${deletedCoinIds.length}개 항목`);
    setIsDeletingCoins(true);

    try {
      console.log('🗑️ 코인 히스토리 삭제 시작:', { 
        studentId: student.id, 
        selectedCount: deletedCoinIds.length 
      });

      // 최신 코인 데이터는 ClassMembersCoin에서 조회
      const coinManagementService = new CoinManagementService(userId, classId, memberGroup);
      const studentCoinData = await coinManagementService.getStudentCoinData(student.id);

      if (!studentCoinData) {
        console.error('❌ 학생 코인 데이터를 찾을 수 없습니다:', student.id);
        return;
      }

      // 선택된 코인 히스토리 제거 및 레벨업 보상 처리
      console.log('🔍 삭제할 코인 ID들:', deletedCoinIds);
      console.log('🔍 현재 코인 히스토리 개수:', studentCoinData.coinsHistory?.length || 0);

      const deletedCoinHistories: CoinHistory[] = [];
      const updatedCoinsHistory = (studentCoinData.coinsHistory || []).filter((history, index) => {
        const historyId = getCoinHistoryId(history);
        const shouldKeep = !deletedCoinIds.includes(historyId);

        if (!shouldKeep) {
          deletedCoinHistories.push(history);
        }

        console.log(`🔍 히스토리 ${index}: ID=${historyId}, 삭제예정=${!shouldKeep}, 유지=${shouldKeep}, 타입=${history.coinType}`);
        return shouldKeep;
      });

      // rewardedLevels는 코인/포인트 회수·삭제 시에도 제거하지 않음 (중복 지급 방지 유지)
      // 한 번 보상 받은 레벨은 기록에 남겨 두어, 재레벨업 시 이미 지급된 보상이 다시 지급되지 않도록 함.
      
      console.log('🔍 필터링 후 코인 히스토리 개수:', updatedCoinsHistory.length);

      const newCoinsTotal = calculateTotalCoins(updatedCoinsHistory);

      // ClassMembersCoin 컬렉션 업데이트
      await coinManagementService.updateStudentCoinData(student.id, newCoinsTotal, updatedCoinsHistory);

      // 로컬 상태 업데이트
      setCurrentCoins(newCoinsTotal);
      setCoinHistory(updatedCoinsHistory);
      setDeletedCoinIds([]);
      setEditingCoinRowId(null);
      setShowCoinDeleteConfirmModal(false);

      console.log('✅ 코인 히스토리 삭제 완료:', { 
        newCoins: newCoinsTotal, 
        historyCount: updatedCoinsHistory.length,
        deletedCount: deletedCoinIds.length
      });

      // 부모 컴포넌트에 데이터 변경 알림
      onDataChange?.();

    } catch (error) {
      console.error('코인 히스토리 삭제 실패:', error);
    } finally {
      setIsDeletingCoins(false);
    }
  };

  // 코인 삭제 취소
  const cancelCoinDelete = () => {
    setShowCoinDeleteConfirmModal(false);
  };

  // 기존 개별 로드 함수들은 loadAllData로 통합됨

  // 모든 데이터를 한 번에 로드하는 함수
  const loadAllData = async (options?: { force?: boolean; reason?: string; silent?: boolean }) => {
    if (!student || !userId || !classId) return;

    const loadKey = `${classId}_${memberGroup || 'none'}_${student.id}`;
    const now = Date.now();
    const cooldownMs = 2000;

    if (!options?.force) {
      if (loadInFlightRef.current) {
        console.log('⏳ 모든 데이터 로드 스킵: 이미 로딩 중', { loadKey, reason: options?.reason });
        return;
      }
      if (lastLoadKeyRef.current === loadKey && now - lastLoadAtRef.current < cooldownMs) {
        console.log('⏳ 모든 데이터 로드 스킵: 최근 로드', {
          loadKey,
          elapsedMs: now - lastLoadAtRef.current,
          reason: options?.reason
        });
        return;
      }
    }

    lastLoadKeyRef.current = loadKey;
    lastLoadAtRef.current = now;

    if (!options?.silent) {
    setIsLoading(true);
    }
    const loadPromise = (async () => {
    try {
        console.log('🚀 모든 데이터 로드 시작:', { studentId: student.id, reason: options?.reason });

      // 1. ClassMembers 데이터를 스마트 캐시 전략으로 로드
      // - force 시(스토어 구매 등): 캐시 무효화 후 DB에서 로드 → 코인과 동일 시점에 아이템 카드 반영
      // - 그 외: 캐시가 있고 최신(5분 이내)이면 캐시 사용
      console.log('🔄 멤버 데이터 스마트 캐시 로드 시작');

      let classMembers: ClassMember[];
      const now = Date.now();
      const CACHE_TTL = 5 * 60 * 1000; // 5분

      let useCache = false;
      let cachedMembers: ClassMember[] | null = null;

      if (options?.force) {
        // 스토어 구매 등 반영 시: 멤버 캐시 무효화 후 DB에서 로드 (코인과 동일하게 최신 데이터 사용)
        userDataService.invalidateMembersCache(userId, classId, memberGroup);
        console.log('🔄 force=true, 멤버 캐시 무효화 후 DB에서 로드');
        classMembers = await userDataService.loadClassMembers(userId, classId, memberGroup);
        console.log('✅ DB에서 데이터 로드 완료 (force)');
      } else {
        const memoryKey = `members_${userId}_${classId}_${memberGroup || 'A'}`;
        const cacheKey = `classMembers_${userId}_${classId}_${memberGroup || 'A'}`;

      // 메모리 캐시 확인
      const memCache = (userDataService as any).__classMembersCache?.get(memoryKey);
      if (memCache && now - memCache.ts < CACHE_TTL) {
        cachedMembers = memCache.members;
        useCache = true;
        console.log('💾 메모리 캐시 사용 (최신):', { count: cachedMembers?.length, age: Math.round((now - memCache.ts) / 1000), unit: '초' });
      }

      // localStorage 캐시 확인 (메모리 캐시에 없으면)
      if (!useCache) {
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            const raw = window.localStorage.getItem(cacheKey);
            if (raw) {
              const parsed = JSON.parse(raw) as { ts: number; members: ClassMember[] };
              if (parsed?.ts && Array.isArray(parsed.members) && now - parsed.ts < CACHE_TTL) {
                cachedMembers = parsed.members;
                useCache = true;
                console.log('💾 localStorage 캐시 사용 (최신):', { count: cachedMembers?.length, age: Math.round((now - parsed.ts) / 1000), unit: '초' });
              }
            }
          }
        } catch (error) {
          console.warn('캐시 확인 실패:', error);
        }
      }

      if (useCache && cachedMembers) {
        classMembers = cachedMembers;
        console.log('✅ 캐시에서 데이터 로드 완료');
      } else {
        console.log('🔄 캐시 없음 또는 오래됨, DB에서 로드');
        classMembers = await userDataService.loadClassMembers(userId, classId, memberGroup);
        console.log('✅ DB에서 데이터 로드 완료');
        }
      }
      const targetMember = classMembers.find(member => member.id === student.id);
      
      if (!targetMember) {
        console.log('⚠️ 해당 멤버를 ClassMembers에서 찾을 수 없음:', student.id);
        return;
      }

      // 2. 코인 데이터 설정 (ClassMembersCoin에서 로드, 없으면 ClassMembers 폴백)
      const coinManagementService = new CoinManagementService(userId, classId, memberGroup);
      const coinData = await coinManagementService.getStudentCoinData(student.id);
      
      if (coinData && coinData.coinsHistory && coinData.coinsHistory.length > 0) {
        // ClassMembersCoin에서 데이터 로드 성공
        setCurrentCoins(coinData.coins || 0);
        setCoinHistory(coinData.coinsHistory);
        console.log('✅ 코인 데이터 로드 (ClassMembersCoin):', { coins: coinData.coins, historyCount: coinData.coinsHistory.length });
      } else {
        // 폴백: ClassMembers에서 로드
      setCurrentCoins(calculateTotalCoins(targetMember.coinsHistory));
      setCoinHistory(targetMember.coinsHistory || []);
        console.log('ℹ️ 코인 데이터 로드 (ClassMembers 폴백):', { coins: calculateTotalCoins(targetMember.coinsHistory), historyCount: targetMember.coinsHistory?.length || 0 });
      }
      
      // 3. 아이템 카드 데이터 설정 (코인과 동일하게 DB에서 직접 로드 → 캐시 없이 최신 반영)
      const itemCardServiceInstance = new ItemCardService(userId, classId, memberGroup);
      let itemCards: any[] = [];
      try {
        const studentCardsFromDb = await itemCardServiceInstance.getStudentItemCards(student.id);
        if (studentCardsFromDb && studentCardsFromDb.length > 0) {
          itemCards = studentCardsFromDb.map((itemCard: any) => ({
        ...itemCard,
            category: itemCard.category || (itemCard.assignmentId ? 'assignment' : 'levelup')
          }));
          console.log('✅ 아이템 카드 로드 (DB 직접):', { count: itemCards.length });
        }
      } catch (itemCardLoadError) {
        console.warn('⚠️ 아이템 카드 DB 로드 실패, ClassMembers 폴백:', itemCardLoadError);
      }
      if (itemCards.length === 0) {
        itemCards = (targetMember.itemCards || []).map((itemCard: any) => ({
          ...itemCard,
          category: itemCard.category || (itemCard.assignmentId ? 'assignment' : 'levelup')
        }));
        console.log('ℹ️ 아이템 카드 로드 (ClassMembers 폴백):', { count: itemCards.length });
      }
      setStudentItemCards(itemCards);
      
      // 4. 아이템 카드 상세 정보 로드
      const itemCardDetailsMap: {[key: string]: any} = {};
      console.log('🔍 아이템 카드 상세 정보 로드 시작:', itemCards.length, '개');

      // 아이템 카드 세트 전체를 한 번에 로드해서 캐시
      let itemCardSet: any = null;
      try {
        itemCardSet = await itemCardServiceInstance.loadItemCardSet();
        console.log('🔍 아이템 카드 세트 로드:', itemCardSet ? '성공' : '실패');
        if (itemCardSet) {
          console.log('🔍 세트별 아이템 수:', {
            levelupItem: itemCardSet.levelupItem?.length || 0,
            assignmentItem: itemCardSet.assignmentItem?.length || 0,
            storeItem: itemCardSet.storeItem?.length || 0
          });
        }
      } catch (error) {
        console.error('아이템 카드 세트 로드 실패:', error);
      }

      for (const itemCard of itemCards) {
        console.log('🔍 처리중인 아이템 카드:', {
          itemCardId: itemCard.itemCardId,
          category: itemCard.category,
          instanceId: itemCard.instanceId,
          isLocked: itemCard.isLocked,
          remainingUseCount: itemCard.remainingUseCount
        });
        if (itemCard.itemCardId) {
          try {
            let itemCardDetail = null;

            // 아이템 카드 세트에서 직접 찾기 (더 효율적)
            if (itemCardSet) {
              const allItems = [
                ...(itemCardSet.levelupItem || []),
                ...(itemCardSet.assignmentItem || []),
                ...(itemCardSet.storeItem || [])
              ];
              itemCardDetail = allItems.find((item: any) => item.itemCardId === itemCard.itemCardId);
              console.log(`🔍 세트에서 직접 검색: ${itemCard.itemCardId} - ${itemCardDetail ? '찾음' : '찾을 수 없음'}`);
            }

            // 세트에서 찾지 못했으면 기존 방식으로 시도
            if (!itemCardDetail) {
              itemCardDetail = await itemCardServiceInstance.getItemCard(itemCard.itemCardId);
              console.log(`🔍 getItemCard 재시도: ${itemCard.itemCardId} - ${itemCardDetail ? '찾음' : '찾을 수 없음'}`);
            }

            if (itemCardDetail) {
              itemCardDetailsMap[itemCard.itemCardId] = itemCardDetail;
              console.log('✅ 아이템 카드 상세 정보 저장됨:', itemCardDetail.name);
            } else {
              console.log('❌ 아이템 카드 상세 정보를 찾을 수 없음:', itemCard.itemCardId);
              // 상세 정보가 없어도 기본 정보로 표시할 수 있도록 처리
              itemCardDetailsMap[itemCard.itemCardId] = {
                itemCardId: itemCard.itemCardId,
                name: `아이템 카드 #${itemCard.itemCardId.slice(-8)}`,
                memo: '아이템 카드 정보를 찾을 수 없습니다.',
                type: itemCard.type || 'passive',
                category: itemCard.category,
                cost: 0
              };
            }
          } catch (error) {
            console.error(`아이템 카드 상세 정보 로드 실패: ${itemCard.itemCardId}`, error);
          }
        }
      }
      console.log('✅ 아이템 카드 상세 정보 로드 완료:', Object.keys(itemCardDetailsMap).length, '개 성공');
      setItemCardDetails(itemCardDetailsMap);
      
      // 5. 과제 포인트 데이터 로드
      const { PointsQueryService } = await import('../services/pointsQueryService');
      const pointsQueryService = new PointsQueryService(userId, classId, memberGroup);
      const pointsTableData = await pointsQueryService.getPointsTableData(classMembers);
      const targetStudent = pointsTableData.students.find(s => s.studentId === student.id);
      setAssignmentData(targetStudent || null);
      
      // 6. 보상 포인트 데이터 로드
      const pointManagementService = new PointManagementService(userId, classId, memberGroup);
      const rewardPointsData = await pointManagementService.getStudentPointHistory(student.id);
      setRewardPoints(rewardPointsData);
      
      console.log('✅ 모든 데이터 로드 완료:', { 
        coins: calculateTotalCoins(targetMember.coinsHistory),
        coinHistoryCount: targetMember.coinsHistory?.length || 0,
        itemCardsCount: itemCards.length,
        itemCardDetailsCount: Object.keys(itemCardDetailsMap).length,
        assignmentData: !!targetStudent,
        rewardPointsCount: rewardPointsData.length
      });
    } catch (error) {
      console.error('모든 데이터 로드 실패:', error);
    } finally {
      setIsLoading(false);
      }
    })();

    loadInFlightRef.current = loadPromise;
    try {
      await loadPromise;
    } finally {
      if (loadInFlightRef.current === loadPromise) {
        loadInFlightRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (isOpen && student) {
      if (lastLoadedStudentIdRef.current !== student.id) {
        lastLoadedStudentIdRef.current = student.id;
        loadAllData({ reason: 'open' });
      setDeletedPointIds([]); // 모달 열 때마다 삭제 목록 초기화
      }
      return;
    }
    if (!isOpen) {
      lastLoadedStudentIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, student?.id]);

  // 모달 열릴 때 배경 스크롤 비활성화
  useEffect(() => {
    if (isOpen) {
      // 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // 모달 닫힐 때 원래 상태로 복원
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // 저장 버튼 활성화 여부
  const hasUnsavedChanges = deletedPointIds.length > 0;

  // 휴지통 클릭 - 즉시 표시만 변경 (삭제 예정 토글)
  const handleDeletePoint = (pointId: string) => {
    if (isDeleting) return;

    if (deletedPointIds.includes(pointId)) {
      // 이미 삭제 예정이면 취소
      setDeletedPointIds(prev => prev.filter(id => id !== pointId));
    } else {
      // 삭제 예정 목록에 추가
      setDeletedPointIds(prev => [...prev, pointId]);
    }
  };

  // 저장 버튼 클릭 - 확인 모달 표시
  const handleSaveClick = () => {
    if (deletedPointIds.length > 0) {
      setShowSaveConfirmModal(true);
    }
  };

  // 실제 저장 (일괄 삭제) 실행
  const confirmSave = async () => {
    if (!student || !userId || !classId) {
      console.log('⚠️ 저장 조건 미충족');
      return;
    }
    
    if (isDeleting) {
      console.log('⚠️ 이미 처리 중입니다.');
      return;
    }

    console.log(`🗑️ 포인트 일괄 삭제 시작: ${deletedPointIds.length}개 항목`);
    setIsDeleting(true);
    
    try {
      // 데이터 기반 필터링으로 삭제할 포인트 찾기
      const pointsToDelete = rewardPoints.filter(point => {
        // 안전한 날짜 변환
        let recordedDate: Date;
        if (point.recordedDate instanceof Date) {
          recordedDate = point.recordedDate;
        } else if (point.recordedDate && typeof point.recordedDate === 'object' && 'toDate' in point.recordedDate) {
          recordedDate = (point.recordedDate as any).toDate();
        } else {
          recordedDate = new Date(point.recordedDate);
        }
        
        const pointId = `${recordedDate.getTime()}_${point.pointValue}_${point.pointMemo}_${point.pointCategory}`;
        return deletedPointIds.includes(pointId);
      });
      
      console.log(`🔍 삭제할 포인트들:`, pointsToDelete.map(p => p.pointMemo));
      
      const pointManagementService = new PointManagementService(userId, classId, memberGroup);
      
      // 삭제용 ID 생성 (서비스에서 비교하는 형식과 동일하게)
      const pointIdsToDelete = pointsToDelete.map(point => {
        // 안전한 날짜 변환
        let recordedDate: Date;
        if (point.recordedDate instanceof Date) {
          recordedDate = point.recordedDate;
        } else if (point.recordedDate && typeof point.recordedDate === 'object' && 'toDate' in point.recordedDate) {
          recordedDate = (point.recordedDate as any).toDate();
        } else {
          recordedDate = new Date(point.recordedDate);
        }
        
        // 서비스와 동일한 형식으로 ID 생성
        return `${recordedDate.getTime()}_${point.pointValue}_${point.pointMemo}_${point.pointCategory}`;
      });
      
      console.log(`🔄 배치 삭제 시작: ${pointIdsToDelete.length}개 포인트`, pointIdsToDelete);
      
      // 삭제 전 총 포인트 계산 (현재 포인트 기록의 합계)
      const oldTotalPoints = rewardPoints.reduce((sum, point) => sum + (point.pointValue || 0), 0);
      
      // 삭제될 포인트들의 합계 계산
      const deletedPointsSum = pointsToDelete.reduce((sum, point) => sum + (point.pointValue || 0), 0);
      
      await pointManagementService.deleteStudentPointsBatch(student.id, pointIdsToDelete);
      
      console.log(`✅ 서버 삭제 완료. 포인트 데이터만 새로고침`);
      
      // 성능 최적화: 포인트 데이터만 리로드 (전체 리로드 X)
      const updatedRewardPoints = await pointManagementService.getStudentPointHistory(student.id);
      setRewardPoints(updatedRewardPoints);
      
      // 삭제 후 총 포인트 계산
      const newTotalPoints = oldTotalPoints - deletedPointsSum;
      
      // 레벨다운 감지 및 보상 회수
      if (userId && classId && oldTotalPoints !== newTotalPoints) {
        try {
          const { syncLevelUpRewardsByPoints } = await import('../utils/levelUpRewardHelper');
          const { PointTypeManagementService } = await import('../services/pointTypeManagementService');
          
          const pointTypeService = new PointTypeManagementService(userId, classId);
          const pointTypeData = await pointTypeService.getPointTypeData();
          const levelUpPoints = pointTypeData.levelUpPoints || 100;
          
          await syncLevelUpRewardsByPoints(
            userId,
            classId,
            student.id,
            oldTotalPoints,
            newTotalPoints,
            levelUpPoints
          );
          
          console.log(`✅ 레벨다운 감지 및 보상 회수 완료: ${oldTotalPoints} -> ${newTotalPoints}`);
        } catch (error) {
          console.error('❌ 레벨다운 보상 회수 실패:', error);
        }
      }
      
      // 상태 초기화
      setDeletedPointIds([]);
      setEditingRowId(null);
      setShowSaveConfirmModal(false);
      
      // 부모 컴포넌트에 데이터 변경 알림 (레벨 정보 업데이트를 위해)
      onDataChange?.();
      
      console.log('✅ 포인트 일괄 삭제 완전 완료 (포인트만 리로드)');
    } catch (error) {
      console.error('❌ 포인트 삭제 실패:', error);
      alert(`포인트 삭제에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    } finally {
      setIsDeleting(false);
      console.log('🔓 isDeleting 상태 해제됨');
    }
  };

  // 저장 확인 모달 닫기
  const cancelSave = () => {
    setShowSaveConfirmModal(false);
  };

  // 행 클릭 핸들러
  const handleRowClick = (pointId: string) => {
    setEditingRowId(editingRowId === pointId ? null : pointId);
  };

  // 날짜를 mm/dd 형식으로 변환하는 헬퍼 함수
  const formatDateToMMDD = (dateValue: any): string => {
    try {
      let date: Date;
      
      // Firestore Timestamp 객체인지 확인
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        date = new Date(dateValue);
      } else {
        return '-';
      }
      
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${month}/${day}`;
    } catch (error) {
      return '-';
    }
  };

  if (!isOpen || !student) return null;

  // 데이터 로딩 중일 때는 로딩 화면 표시
  if (isLoading) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <LoadingContainer>
            <LoadingSpinner>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="var(--color-border)" strokeWidth="2"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--color-accent-strong)" strokeWidth="2" strokeLinecap="round">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    dur="1s"
                    repeatCount="indefinite"
                    values="0 12 12;360 12 12"
                  />
                </path>
              </svg>
            </LoadingSpinner>
            <LoadingText>데이터를 로딩 중입니다...</LoadingText>
          </LoadingContainer>
        </ModalContent>
      </ModalOverlay>
    );
  }

  // 마감일 표시용 포맷 (ISO 또는 날짜 문자열 → mm/dd)
  const formatDueDateDisplay = (dueDate: string | undefined): string => {
    if (!dueDate) return '-';
    const date = new Date(dueDate);
    if (isNaN(date.getTime())) return dueDate;
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${m}/${d}`;
  };

  // 필터링된 과제 (년도 포함 마감일 기준 최신순 정렬, 0점인 항목 제외)
  const filteredAssignments = (assignmentData?.assignments?.filter(assignment => {
    // 0점인 항목은 제외
    if ((assignment.points || 0) === 0) return false;
    // 타입 필터 적용
    if (assignmentFilter === 'all') return true;
    return assignment.type === assignmentFilter;
  }) || []).sort((a, b) => {
    const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
    const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
    return dateB - dateA; // 최신순 (년도 포함)
  });

  // 과제 포인트 페이지네이션
  const totalAssignmentPages = Math.ceil(filteredAssignments.length / ITEMS_PER_PAGE);
  const paginatedAssignments = filteredAssignments.slice(
    (assignmentPage - 1) * ITEMS_PER_PAGE,
    assignmentPage * ITEMS_PER_PAGE
  );
  // 페이지네이션 활성 시 항상 ITEMS_PER_PAGE개 행으로 채워 모달 크기 고정
  const assignmentRowsForRender =
    totalAssignmentPages > 1
      ? [...paginatedAssignments, ...Array(Math.max(0, ITEMS_PER_PAGE - paginatedAssignments.length)).fill(null)]
      : paginatedAssignments;

  // 안전한 타임스탬프 추출 헬퍼 함수
  const getTimestamp = (recordedDate: any): number => {
    if (!recordedDate) return Date.now();
    
    // Date 객체인 경우
    if (recordedDate instanceof Date) {
      return recordedDate.getTime();
    }
    
    // Firestore Timestamp인 경우
    if (recordedDate.toDate && typeof recordedDate.toDate === 'function') {
      return recordedDate.toDate().getTime();
    }
    
    // 문자열인 경우
    if (typeof recordedDate === 'string') {
      const date = new Date(recordedDate);
      return isNaN(date.getTime()) ? Date.now() : date.getTime();
    }
    
    // 숫자인 경우 (timestamp)
    if (typeof recordedDate === 'number') {
      return recordedDate;
    }
    
    return Date.now();
  };

  // 필터링된 긍정행동 포인트 (최근 날짜순 정렬)
  const filteredPositivePoints = rewardPoints.filter(point => {
    if (point.pointCategory !== 'positive') return false;
    if (!positiveSearchFilter.trim()) return true;
    return point.pointMemo.toLowerCase().includes(positiveSearchFilter.toLowerCase());
  }).sort((a, b) => {
    const timestampA = getTimestamp(a.recordedDate);
    const timestampB = getTimestamp(b.recordedDate);
    return timestampB - timestampA; // 최근 날짜가 위로 (내림차순)
  });

  // 필터링된 행동체크 포인트 (최근 날짜순 정렬)
  const filteredNegativePoints = rewardPoints.filter(point => {
    if (point.pointCategory !== 'negative') return false;
    if (!negativeSearchFilter.trim()) return true;
    return point.pointMemo.toLowerCase().includes(negativeSearchFilter.toLowerCase());
  }).sort((a, b) => {
    const timestampA = getTimestamp(a.recordedDate);
    const timestampB = getTimestamp(b.recordedDate);
    return timestampB - timestampA; // 최근 날짜가 위로 (내림차순)
  });

  // 긍정행동 포인트 페이지네이션
  const totalPositivePages = Math.ceil(filteredPositivePoints.length / ITEMS_PER_PAGE);
  const paginatedPositivePoints = filteredPositivePoints.slice(
    (positivePage - 1) * ITEMS_PER_PAGE,
    positivePage * ITEMS_PER_PAGE
  );
  const positiveRowsForRender =
    totalPositivePages > 1
      ? [...paginatedPositivePoints, ...Array(Math.max(0, ITEMS_PER_PAGE - paginatedPositivePoints.length)).fill(null)]
      : paginatedPositivePoints;

  // 행동체크 포인트 페이지네이션
  const totalNegativePages = Math.ceil(filteredNegativePoints.length / ITEMS_PER_PAGE);
  const paginatedNegativePoints = filteredNegativePoints.slice(
    (negativePage - 1) * ITEMS_PER_PAGE,
    negativePage * ITEMS_PER_PAGE
  );
  const negativeRowsForRender =
    totalNegativePages > 1
      ? [...paginatedNegativePoints, ...Array(Math.max(0, ITEMS_PER_PAGE - paginatedNegativePoints.length)).fill(null)]
      : paginatedNegativePoints;

  // 코인 히스토리 필터링 및 정렬
  const filteredCoinHistory = (coinHistory || [])
    .filter((history: CoinHistory) =>
      !coinSearchFilter ||
      history.earnedCoinMemo.includes(coinSearchFilter)
    )
    .sort((a: CoinHistory, b: CoinHistory) => {
      // 날짜 내림차순 정렬 (최신 데이터가 상단)
      let dateA: Date;
      let dateB: Date;
        
      // Firestore Timestamp 객체 처리
      if (a.earnedAt && typeof a.earnedAt === 'object' && 'toDate' in a.earnedAt) {
        dateA = (a.earnedAt as any).toDate();
      } else if (a.earnedAt instanceof Date) {
        dateA = a.earnedAt;
      } else {
        dateA = new Date(a.earnedAt);
      }
      
      if (b.earnedAt && typeof b.earnedAt === 'object' && 'toDate' in b.earnedAt) {
        dateB = (b.earnedAt as any).toDate();
      } else if (b.earnedAt instanceof Date) {
        dateB = b.earnedAt;
      } else {
        dateB = new Date(b.earnedAt);
      }
      
      return dateB.getTime() - dateA.getTime();
    });

  // 코인 히스토리 페이지네이션
  const totalCoinPages = Math.ceil(filteredCoinHistory.length / ITEMS_PER_PAGE);
  const paginatedCoinHistory = filteredCoinHistory.slice(
    (coinPage - 1) * ITEMS_PER_PAGE,
    coinPage * ITEMS_PER_PAGE
  );
  const coinRowsForRender =
    totalCoinPages > 1
      ? [...paginatedCoinHistory, ...Array(Math.max(0, ITEMS_PER_PAGE - paginatedCoinHistory.length)).fill(null)]
      : paginatedCoinHistory;

  // 요약 정보 계산
  const calculateSummary = () => {
    // 과제 포인트 요약
    const assignmentPoints = {
      general: 0,
      project: 0,
      routine: 0,
      total: 0
    };

    // 과제 포인트 계산
    if (assignmentData?.assignments) {
      assignmentData.assignments.forEach(assignment => {
        const points = assignment.points || 0;
        assignmentPoints.total += points;
        
        switch (assignment.type) {
          case 'general':
            assignmentPoints.general += points;
            break;
          case 'project':
            assignmentPoints.project += points;
            break;
          case 'routine':
            assignmentPoints.routine += points;
            break;
        }
      });
    }

    // 긍정행동 포인트 요약
    const positivePoints = {
      sets: {} as Record<string, number>,
      others: 0,
      total: 0
    };

    // 행동체크 포인트 요약
    const negativePoints = {
      sets: {} as Record<string, number>,
      others: 0,
      total: 0
    };

    rewardPoints.forEach(point => {
      const points = point.pointValue || 0;
      
      if (point.pointCategory === 'positive') {
        positivePoints.total += points;
        
        if (point.pointType === '긍정행동' && point.pointMemo) {
          // 세트별 분류 (메모에서 세트 정보 추출)
          const setMatch = point.pointMemo.match(/세트(\d+)/);
          if (setMatch) {
            const setNum = setMatch[1];
            const setKey = `세트${setNum}`;
            positivePoints.sets[setKey] = (positivePoints.sets[setKey] || 0) + points;
          } else {
            positivePoints.others += points;
          }
        } else {
          positivePoints.others += points;
        }
      } else if (point.pointCategory === 'negative') {
        negativePoints.total += points;
        
        if (point.pointType === '행동체크' && point.pointMemo) {
          // 세트별 분류 (메모에서 세트 정보 추출)
          const setMatch = point.pointMemo.match(/세트(\d+)/);
          if (setMatch) {
            const setNum = setMatch[1];
            const setKey = `세트${setNum}`;
            negativePoints.sets[setKey] = (negativePoints.sets[setKey] || 0) + points;
          } else {
            negativePoints.others += points;
          }
        } else {
          negativePoints.others += points;
        }
      }
    });

    return {
      assignment: assignmentPoints,
      positive: positivePoints,
      negative: negativePoints
    };
  };

  const summary = calculateSummary();

  // 날짜 포맷팅
  const formatDate = (date: any) => {
    if (!date) return '-';
    
    let actualDate: Date;
    
    if (date instanceof Date) {
      actualDate = date;
    } else if (typeof date === 'string') {
      actualDate = new Date(date);
    } else if (date && typeof date.toDate === 'function') {
      // Firestore Timestamp
      actualDate = date.toDate();
    } else {
      return '-';
    }
    
    // 유효한 날짜인지 확인
    if (isNaN(actualDate.getTime())) {
      return '-';
    }
    
    const month = (actualDate.getMonth() + 1).toString().padStart(2, '0');
    const day = actualDate.getDate().toString().padStart(2, '0');
    return `${month}/${day}`;
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderContent>
            <HeaderRow>
              <StudentInfo>
                <StudentNumber>{student.number}</StudentNumber>
                <StudentNameText>{student.name}</StudentNameText>
              </StudentInfo>
              <HeaderCenterActions>
                <StoreButton onClick={handleOpenStoreModal} title="스토어">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="var(--store-icon-color)" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z" />
                  </svg>
                </StoreButton>
              </HeaderCenterActions>
              <HeaderActions>
            <CloseButton onClick={onClose}>×</CloseButton>
              </HeaderActions>
            </HeaderRow>
          </HeaderContent>
        </ModalHeader>

        {/* 메인 탭 */}
        <MainTabContainer>
          <MainTabButton 
            $isActive={mainTab === 'points'}
            onClick={() => setMainTab('points')}
          >
            포인트
          </MainTabButton>
          <MainTabButton 
            $isActive={mainTab === 'coins'}
            onClick={() => setMainTab('coins')}
          >
            코인
          </MainTabButton>
          {/* 임시: 모든 플랜에서 아이템 카드 탭 표시 */}
          <MainTabButton
            $isActive={mainTab === 'itemcards'}
            onClick={() => setMainTab('itemcards')}
          >
            아이템 카드
          </MainTabButton>
        </MainTabContainer>

        {/* 포인트 탭 내용 */}
        {mainTab === 'points' && (
          <>
            {/* 포인트 요약 */}
            <TabSummaryContainer>
              <TabSummaryTitle>총 포인트</TabSummaryTitle>
              <TabSummaryContent>
                <PointBadge>P</PointBadge>
                <TabSummaryValue>{summary.assignment.total + summary.positive.total}점</TabSummaryValue>
              </TabSummaryContent>
            </TabSummaryContainer>


        <PointsButtonContainer>
          <PointsSlider $position={
            activeTab === 'assignment' ? 0 : 
            activeTab === 'positive' ? 1 : 
            activeTab === 'negative' ? 2 : 0
          } />
          <PointsButton 
            $isActive={activeTab === 'assignment'}
            onClick={() => setActiveTab('assignment')}
          >
            <PointsButtonLeft>
              <span className="full-text">과제 포인트</span>
              <span className="short-text">과제P</span>
            </PointsButtonLeft>
            <PointsButtonRight $isActive={activeTab === 'assignment'}>
              {summary.assignment.total}점
            </PointsButtonRight>
          </PointsButton>
          
          <PointsButton 
            $isActive={activeTab === 'positive'}
            onClick={() => setActiveTab('positive')}
          >
            <PointsButtonLeft>
              <span className="full-text">긍정행동 포인트</span>
              <span className="short-text">긍정행동P</span>
            </PointsButtonLeft>
            <PointsButtonRight $isActive={activeTab === 'positive'}>
              {summary.positive.total}점
            </PointsButtonRight>
          </PointsButton>
          
          <PointsButton 
            $isActive={activeTab === 'negative'}
            onClick={() => setActiveTab('negative')}
          >
            <PointsButtonLeft>
              <span className="full-text">행동체크 포인트</span>
              <span className="short-text">행동체크P</span>
            </PointsButtonLeft>
            <PointsButtonRight $isActive={activeTab === 'negative'}>
              {summary.negative.total}점
            </PointsButtonRight>
          </PointsButton>
        </PointsButtonContainer>

        {activeTab && (
        <TabContent>
          {activeTab === 'assignment' ? (
            <AssignmentTab>
              <FilterContainer>
                <FilterButton 
                  $isActive={assignmentFilter === 'all'} 
                  onClick={() => setAssignmentFilter('all')}
                >
                    <FilterButtonLeft>
                      <span className="full-text">과제 전체</span>
                      <span className="short-text">전체</span>
                    </FilterButtonLeft>
                    <FilterButtonRight>
                      {summary.assignment.total}점
                    </FilterButtonRight>
                </FilterButton>
                <FilterButton 
                  $isActive={assignmentFilter === 'general'} 
                  onClick={() => setAssignmentFilter('general')}
                >
                    <FilterButtonLeft>
                      <span className="full-text">일반 과제</span>
                      <span className="short-text">일반</span>
                    </FilterButtonLeft>
                    <FilterButtonRight>
                      {summary.assignment.general}점
                    </FilterButtonRight>
                </FilterButton>
                <FilterButton 
                  $isActive={assignmentFilter === 'project'} 
                  onClick={() => setAssignmentFilter('project')}
                >
                    <FilterButtonLeft>
                      <span className="full-text">프로젝트 과제</span>
                      <span className="short-text">프로젝트</span>
                    </FilterButtonLeft>
                    <FilterButtonRight>
                      {summary.assignment.project}점
                    </FilterButtonRight>
                </FilterButton>
                <FilterButton 
                  $isActive={assignmentFilter === 'routine'} 
                  onClick={() => setAssignmentFilter('routine')}
                >
                    <FilterButtonLeft>
                      <span className="full-text">루틴 과제</span>
                      <span className="short-text">루틴</span>
                    </FilterButtonLeft>
                    <FilterButtonRight>
                      {summary.assignment.routine}점
                    </FilterButtonRight>
                </FilterButton>
              </FilterContainer>
              <AssignmentsTable>
                <TableHeader>
                  <HeaderCell columnIndex={0}>타입</HeaderCell>
                  <HeaderCell columnIndex={1}>마감일</HeaderCell>
                  <HeaderCell columnIndex={2}>과제명</HeaderCell>
                  <HeaderCell columnIndex={3}>포인트</HeaderCell>
                  <HeaderCell columnIndex={4}>메모</HeaderCell>
                </TableHeader>
                
                {assignmentRowsForRender.map((assignment, index) =>
                  assignment ? (
                  <TableRow key={index}>
                    <TableCell columnIndex={0}>
                      <TypeBadge type={assignment.type}>
                        {assignment.type === 'general' ? 'G' : 
                         assignment.type === 'project' ? 'P' : 'R'}
                      </TypeBadge>
                    </TableCell>
                    <TableCell columnIndex={1}>
                        {formatDueDateDisplay(assignment.dueDate)}
                    </TableCell>
                    <TableCell columnIndex={2}>
                      {assignment.title}
                    </TableCell>
                    <TableCell columnIndex={3}>
                      <PointsText points={assignment.points} status={assignment.status}>{assignment.points}점</PointsText>
                    </TableCell>
                    <TableCell columnIndex={4}>
                      {assignment.memo || '-'}
                    </TableCell>
                  </TableRow>
                  ) : (
                    <TableRow key={`empty-${index}`} aria-hidden="true">
                      <TableCell columnIndex={0}></TableCell>
                      <TableCell columnIndex={1}></TableCell>
                      <TableCell columnIndex={2}></TableCell>
                      <TableCell columnIndex={3}></TableCell>
                      <TableCell columnIndex={4}></TableCell>
                    </TableRow>
                  )
                )}
              </AssignmentsTable>
              
              {/* 페이지네이션 컨트롤 */}
              {totalAssignmentPages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    onClick={() => setAssignmentPage(prev => Math.max(1, prev - 1))}
                    disabled={assignmentPage === 1}
                    title="이전"
                    aria-label="이전 페이지"
                  >
                    &lt;
                  </PaginationButton>
                  <PaginationInfo>{assignmentPage}/{totalAssignmentPages}</PaginationInfo>
                  <PaginationButton
                    onClick={() => setAssignmentPage(prev => Math.min(totalAssignmentPages, prev + 1))}
                    disabled={assignmentPage === totalAssignmentPages}
                    title="다음"
                    aria-label="다음 페이지"
                  >
                    &gt;
                  </PaginationButton>
                </PaginationContainer>
              )}
            </AssignmentTab>
          ) : activeTab === 'positive' ? (
            <PositiveTab>
              <SearchContainer>
                <SearchInput
                  type="text"
                  placeholder="메모 검색..."
                  value={positiveSearchText}
                  onChange={(e) => setPositiveSearchText(e.target.value)}
                />
                <SearchButton onClick={() => setPositiveSearchFilter(positiveSearchText)}>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchButton>
                <PointDeleteButton 
                  onClick={handleSaveClick}
                  disabled={!hasUnsavedChanges}
                  $hasChanges={hasUnsavedChanges}
                >
                  삭제
                </PointDeleteButton>
              </SearchContainer>

              <RewardTable>
                <RewardTableHeader>
                  <RewardHeaderCell columnIndex={0}>타입</RewardHeaderCell>
                  <RewardHeaderCell columnIndex={1}>날짜</RewardHeaderCell>
                  <RewardHeaderCell columnIndex={2}>메모</RewardHeaderCell>
                  <RewardHeaderCell columnIndex={3}>포인트</RewardHeaderCell>
                </RewardTableHeader>
                
                {isLoading ? (
                  <RewardTableRow $isEmptyState={true}>
                    <RewardTableCell columnIndex={0} colSpan={4}>
                      로딩 중...
                    </RewardTableCell>
                  </RewardTableRow>
                ) : filteredPositivePoints.length === 0 ? (
                  <RewardTableRow $isEmptyState={true}>
                    <RewardTableCell columnIndex={0} colSpan={4}>
                      긍정행동 포인트 기록이 없습니다.
                    </RewardTableCell>
                  </RewardTableRow>
                ) : (
                  positiveRowsForRender.map((point, index) => {
                    if (!point) {
                      return (
                        <RewardTableRow key={`empty-${index}`} $isEmptyState={true} aria-hidden="true">
                          <RewardTableCell columnIndex={0} colSpan={4}></RewardTableCell>
                        </RewardTableRow>
                      );
                    }
                    // 안전한 날짜 변환
                    let recordedDate: Date;
                    if (point.recordedDate instanceof Date) {
                      recordedDate = point.recordedDate;
                    } else if (point.recordedDate && typeof point.recordedDate === 'object' && 'toDate' in point.recordedDate) {
                      recordedDate = (point.recordedDate as any).toDate();
                    } else {
                      recordedDate = new Date(point.recordedDate);
                    }
                    
                    // 데이터 기반 고유 ID 생성
                    const pointId = `${recordedDate.getTime()}_${point.pointValue}_${point.pointMemo}_${point.pointCategory}`;
                    const isEditing = editingRowId === pointId;
                    const isDeleted = deletedPointIds.includes(pointId);
                    
                    return (
                    <RewardTableRow 
                      key={index} 
                        onClick={() => handleRowClick(pointId)}
                        $isDeleted={isDeleted}
                    >
                        <EditableTableCell columnIndex={0} $isEditing={isEditing}>
                        <GemIcon 
                          color="red" 
                          size={20} 
                        />
                          {(isEditing || isDeleted) && (
                          <DeleteButton 
                            onClick={(e) => {
                              e.stopPropagation();
                                handleDeletePoint(pointId);
                            }}
                            disabled={isDeleting}
                            $hasChanges={!isDeleted}
                            $isDeleted={isDeleted}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </DeleteButton>
                        )}
                      </EditableTableCell>
                        <EditableTableCell columnIndex={1} $isEditing={isEditing}>
                        {formatDate(point.recordedDate)}
                      </EditableTableCell>
                        <EditableTableCell columnIndex={2} $isEditing={isEditing}>
                        <MemoWithTimeTag>
                          {point.timeTag && (
                            <TimeTagBadge>
                              {point.timeTag.startsWith('일반 ') ? point.timeTag.replace('일반 ', '') : point.timeTag}
                            </TimeTagBadge>
                          )}
                          <MemoText>{point.pointMemo}</MemoText>
                        </MemoWithTimeTag>
                      </EditableTableCell>
                        <EditableTableCell columnIndex={3} $isEditing={isEditing}>
                        <RewardPointsText $digitCount={point.pointValue.toString().length}>
                          {point.pointValue}점
                        </RewardPointsText>
                      </EditableTableCell>
                    </RewardTableRow>
                    );
                  })
                )}
              </RewardTable>
              
              {/* 페이지네이션 컨트롤 */}
              {totalPositivePages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    onClick={() => setPositivePage(prev => Math.max(1, prev - 1))}
                    disabled={positivePage === 1}
                    title="이전"
                    aria-label="이전 페이지"
                  >
                    &lt;
                  </PaginationButton>
                  <PaginationInfo>{positivePage}/{totalPositivePages}</PaginationInfo>
                  <PaginationButton
                    onClick={() => setPositivePage(prev => Math.min(totalPositivePages, prev + 1))}
                    disabled={positivePage === totalPositivePages}
                    title="다음"
                    aria-label="다음 페이지"
                  >
                    &gt;
                  </PaginationButton>
                </PaginationContainer>
              )}
            </PositiveTab>
          ) : (
            <NegativeTab>
              <SearchContainer>
                <SearchInput
                  type="text"
                  placeholder="메모 검색..."
                  value={negativeSearchText}
                  onChange={(e) => setNegativeSearchText(e.target.value)}
                />
                <SearchButton onClick={() => setNegativeSearchFilter(negativeSearchText)}>
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchButton>
                <PointDeleteButton 
                  onClick={handleSaveClick}
                  disabled={!hasUnsavedChanges}
                  $hasChanges={hasUnsavedChanges}
                >
                  삭제
                </PointDeleteButton>
              </SearchContainer>

              <RewardTable>
                <RewardTableHeader>
                  <RewardHeaderCell columnIndex={0}>타입</RewardHeaderCell>
                  <RewardHeaderCell columnIndex={1}>날짜</RewardHeaderCell>
                  <RewardHeaderCell columnIndex={2}>메모</RewardHeaderCell>
                  <RewardHeaderCell columnIndex={3}>포인트</RewardHeaderCell>
                </RewardTableHeader>
                
                {isLoading ? (
                  <RewardTableRow $isEmptyState={true}>
                    <RewardTableCell columnIndex={0} colSpan={4}>
                      로딩 중...
                    </RewardTableCell>
                  </RewardTableRow>
                ) : filteredNegativePoints.length === 0 ? (
                  <RewardTableRow $isEmptyState={true}>
                    <RewardTableCell columnIndex={0} colSpan={4}>
                      행동체크 포인트 기록이 없습니다.
                    </RewardTableCell>
                  </RewardTableRow>
                ) : (
                  negativeRowsForRender.map((point, index) => {
                    if (!point) {
                      return (
                        <RewardTableRow key={`empty-${index}`} $isEmptyState={true} aria-hidden="true">
                          <RewardTableCell columnIndex={0} colSpan={4}></RewardTableCell>
                        </RewardTableRow>
                      );
                    }
                    // 안전한 날짜 변환
                    let recordedDate: Date;
                    if (point.recordedDate instanceof Date) {
                      recordedDate = point.recordedDate;
                    } else if (point.recordedDate && typeof point.recordedDate === 'object' && 'toDate' in point.recordedDate) {
                      recordedDate = (point.recordedDate as any).toDate();
                    } else {
                      recordedDate = new Date(point.recordedDate);
                    }
                    
                    // 데이터 기반 고유 ID 생성
                    const pointId = `${recordedDate.getTime()}_${point.pointValue}_${point.pointMemo}_${point.pointCategory}`;
                    const isEditing = editingRowId === pointId;
                    const isDeleted = deletedPointIds.includes(pointId);
                    
                    return (
                    <RewardTableRow 
                      key={index} 
                        onClick={() => handleRowClick(pointId)}
                        $isDeleted={isDeleted}
                    >
                        <EditableTableCell columnIndex={0} $isEditing={isEditing}>
                        <GemIcon 
                          color="gray" 
                          size={20} 
                        />
                          {(isEditing || isDeleted) && (
                          <DeleteButton 
                            onClick={(e) => {
                              e.stopPropagation();
                                handleDeletePoint(pointId);
                            }}
                            disabled={isDeleting}
                            $hasChanges={!isDeleted}
                            $isDeleted={isDeleted}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </DeleteButton>
                        )}
                      </EditableTableCell>
                        <EditableTableCell columnIndex={1} $isEditing={isEditing}>
                        {formatDate(point.recordedDate)}
                      </EditableTableCell>
                        <EditableTableCell columnIndex={2} $isEditing={isEditing}>
                        <MemoWithTimeTag>
                          {point.timeTag && (
                            <TimeTagBadge>
                              {point.timeTag.startsWith('일반 ') ? point.timeTag.replace('일반 ', '') : point.timeTag}
                            </TimeTagBadge>
                          )}
                          <MemoText>{point.pointMemo}</MemoText>
                        </MemoWithTimeTag>
                      </EditableTableCell>
                        <EditableTableCell columnIndex={3} $isEditing={isEditing}>
                        <RewardPointsText $digitCount={point.pointValue.toString().length}>
                          {point.pointValue}점
                        </RewardPointsText>
                      </EditableTableCell>
                    </RewardTableRow>
                    );
                  })
                )}
              </RewardTable>
              
              {/* 페이지네이션 컨트롤 */}
              {totalNegativePages > 1 && (
                <PaginationContainer>
                  <PaginationButton
                    onClick={() => setNegativePage(prev => Math.max(1, prev - 1))}
                    disabled={negativePage === 1}
                    title="이전"
                    aria-label="이전 페이지"
                  >
                    &lt;
                  </PaginationButton>
                  <PaginationInfo>{negativePage}/{totalNegativePages}</PaginationInfo>
                  <PaginationButton
                    onClick={() => setNegativePage(prev => Math.min(totalNegativePages, prev + 1))}
                    disabled={negativePage === totalNegativePages}
                    title="다음"
                    aria-label="다음 페이지"
                  >
                    &gt;
                  </PaginationButton>
                </PaginationContainer>
              )}
            </NegativeTab>
          )}
        </TabContent>
        )}
          </>
        )}

        {/* 코인 탭 내용 */}
        {mainTab === 'coins' && (
          <>
            {/* 코인 요약 */}
            <TabSummaryContainer>
              <TabSummaryTitle>보유 코인</TabSummaryTitle>
              <TabSummaryContent>
                <CoinIcon>
                  <svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="8" fill="#FFD700" stroke="#FFA500" strokeWidth="1.5"/>
                    <circle cx="10" cy="10" r="5" fill="#FFA500" opacity="0.3"/>
                    <text x="10" y="13" fontSize="10" fontWeight="bold" fill="#FFA500" textAnchor="middle">₩</text>
                  </svg>
                </CoinIcon>
                <TabSummaryValue>{currentCoins}</TabSummaryValue>
              </TabSummaryContent>
            </TabSummaryContainer>

            <CoinTabContent>
              <CoinButtonContainer>
                <CoinAddButton onClick={() => setShowCoinAddModal(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  코인 추가
                </CoinAddButton>
                <CoinUseButton onClick={() => setShowCoinUseModal(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  코인 사용
                </CoinUseButton>
              </CoinButtonContainer>
              
              <CoinSearchContainer>
              <CoinSearchInput
                type="text"
                placeholder="메모 검색..."
                value={coinSearchText}
                onChange={(e) => setCoinSearchText(e.target.value)}
              />
              <CoinSearchButton onClick={() => setCoinSearchFilter(coinSearchText)}>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                  <path 
                    d="M21 21l-4.35-4.35" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </CoinSearchButton>
              <CoinDeleteButton 
                onClick={handleCoinDeleteClick}
                disabled={deletedCoinIds.length === 0}
                $hasChanges={deletedCoinIds.length > 0}
              >
                삭제
              </CoinDeleteButton>
            </CoinSearchContainer>

            <CoinTable>
              <CoinTableHeader>
                <CoinHeaderCell columnIndex={0}>타입</CoinHeaderCell>
                <CoinHeaderCell columnIndex={1}>날짜</CoinHeaderCell>
                <CoinHeaderCell columnIndex={2}>메모</CoinHeaderCell>
                <CoinHeaderCell columnIndex={3}>획득 코인</CoinHeaderCell>
              </CoinTableHeader>
              <CoinTableBody>
                {coinHistory && coinHistory.length > 0 ? (
                coinRowsForRender.map((history: CoinHistory | null, index: number) => {
                      if (!history) {
                        return (
                          <CoinTableRow key={`empty-${index}`} $isEmptyState={true} aria-hidden="true">
                            <CoinCell columnIndex={0}></CoinCell>
                            <CoinCell columnIndex={1}></CoinCell>
                            <CoinCell columnIndex={2}></CoinCell>
                            <CoinCell columnIndex={3}></CoinCell>
                          </CoinTableRow>
                        );
                      }
                      const historyId = getCoinHistoryId(history);
                      
                      const isEditing = editingCoinRowId === historyId;
                      const isDeleted = deletedCoinIds.includes(historyId);
                      
                      return (
                        <CoinTableRow
                          key={index}
                          onClick={() => handleCoinRowClick(historyId)}
                          $isDeleted={isDeleted}
                          style={{ cursor: 'pointer' }}
                        >
                          {/* 타입 셀 */}
                          <CoinCell columnIndex={0}>
                            {(isEditing || isDeleted) && (
                              <DeleteButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCoin(historyId);
                                }}
                                $hasChanges={!isDeleted}
                                $isDeleted={isDeleted}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </DeleteButton>
                            )}
                            {!(isEditing || isDeleted) && (
                              <CoinTypeBadge $coinType={history.coinType || 'General'}>
                                {history.coinType === 'Levelup' ? 'L' : history.coinType === 'Store' ? 'S' : history.coinType === 'Assignment' ? 'A' : 'G'}
                              </CoinTypeBadge>
                            )}
                          </CoinCell>

                          {/* 날짜 셀 */}
                          <CoinCell columnIndex={1}>
                            {formatDateToMMDD(history.earnedAt)}
                          </CoinCell>

                          {/* 메모 셀 */}
                          <CoinCell columnIndex={2}>
                            {history.earnedCoinMemo}
                          </CoinCell>

                          {/* 획득 코인 셀 */}
                          <CoinCell columnIndex={3} $coinValue={history.earnedCoin}>
                            {history.earnedCoin}
                          </CoinCell>
                        </CoinTableRow>
                      );
                    })
                ) : (
                  <CoinTableRow $isEmptyState={true}>
                    <CoinCell colSpan={4} columnIndex={0}>코인 획득 내역이 없습니다.</CoinCell>
                  </CoinTableRow>
                )}
              </CoinTableBody>
            </CoinTable>
            
            {/* 페이지네이션 컨트롤 */}
            {totalCoinPages > 1 && (
              <PaginationContainer>
                <PaginationButton
                  onClick={() => setCoinPage(prev => Math.max(1, prev - 1))}
                  disabled={coinPage === 1}
                  title="이전"
                  aria-label="이전 페이지"
                >
                  &lt;
                </PaginationButton>
                <PaginationInfo>{coinPage}/{totalCoinPages}</PaginationInfo>
                <PaginationButton
                  onClick={() => setCoinPage(prev => Math.min(totalCoinPages, prev + 1))}
                  disabled={coinPage === totalCoinPages}
                  title="다음"
                  aria-label="다음 페이지"
                >
                  &gt;
                </PaginationButton>
              </PaginationContainer>
            )}
            </CoinTabContent>
          </>
        )}

        {/* 아이템 카드 탭 내용 */}
        {mainTab === 'itemcards' && (
          <>
            {/* 아이템 카드 요약 */}
            <ItemCardSummaryRow>
              <ItemCardSummaryItem>
              <TabSummaryTitle>보유 아이템 카드</TabSummaryTitle>
              <TabSummaryContent>
                <CardIcon>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                    <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                    <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                  </svg>
                </CardIcon>
                  <TabSummaryValue>
                    {studentItemCards.filter(c => {
                      // 잠금된 카드는 카운트에서 제외
                      if (c.isLocked === true) return false;
                      return c.type !== 'active' || (c.remainingUseCount || 0) > 0;
                    }).length}개
                  </TabSummaryValue>
              </TabSummaryContent>
              </ItemCardSummaryItem>
              <ItemCardSummaryItem>
                <TabSummaryTitle>사용완료 카드</TabSummaryTitle>
                <TabSummaryContent>
                  <CardIcon $exhausted>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-border)" stroke="#999" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                      <rect x="6" y="4" width="12" height="16" rx="2" fill="#e8e8e8" stroke="#999" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                      <rect x="10" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#999" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                    </svg>
                  </CardIcon>
                  <TabSummaryValue $exhausted>
                    {studentItemCards.filter(c => {
                      // 잠금된 카드는 카운트에서 제외
                      if (c.isLocked === true) return false;
                      return c.type === 'active' && (c.remainingUseCount || 0) === 0;
                    }).length}개
                  </TabSummaryValue>
                </TabSummaryContent>
              </ItemCardSummaryItem>
            </ItemCardSummaryRow>

            <ItemCardTabContent>
              {/* 타입별 필터 - 이전 형태 (슬라이더) */}
              <ItemCardTypeFilterContainer>
                <ItemCardTypeFilterSlider $position={itemCardFilter === 'all' ? 0 : itemCardFilter === 'passive' ? 1 : 2} />
                <ItemCardTypeFilterButton 
                  $isActive={itemCardFilter === 'all'} 
                  onClick={() => setItemCardFilter('all')}
                >
                  <ItemCardTypeFilterButtonLeft>
                    <span className="full-text">전체 카드</span>
                    <span className="short-text">전체</span>
                  </ItemCardTypeFilterButtonLeft>
                  <ItemCardTypeFilterButtonRight $isActive={itemCardFilter === 'all'}>
                    {studentItemCards.filter(card => card.isLocked !== true).length}개
                  </ItemCardTypeFilterButtonRight>
                </ItemCardTypeFilterButton>
                <ItemCardTypeFilterButton 
                  $isActive={itemCardFilter === 'passive'} 
                  onClick={() => setItemCardFilter('passive')}
                >
                  <ItemCardTypeFilterButtonLeft>
                    <span className="full-text">패시브 카드</span>
                    <span className="short-text">패시브</span>
                  </ItemCardTypeFilterButtonLeft>
                  <ItemCardTypeFilterButtonRight $isActive={itemCardFilter === 'passive'}>
                    {studentItemCards.filter(card => card.isLocked !== true && card.type === 'passive').length}개
                  </ItemCardTypeFilterButtonRight>
                </ItemCardTypeFilterButton>
                <ItemCardTypeFilterButton 
                  $isActive={itemCardFilter === 'active'} 
                  onClick={() => setItemCardFilter('active')}
                >
                  <ItemCardTypeFilterButtonLeft>
                    <span className="full-text">액티브 카드</span>
                    <span className="short-text">액티브</span>
                  </ItemCardTypeFilterButtonLeft>
                  <ItemCardTypeFilterButtonRight $isActive={itemCardFilter === 'active'}>
                    {studentItemCards.filter(card => card.isLocked !== true && card.type === 'active').length}개
                  </ItemCardTypeFilterButtonRight>
                </ItemCardTypeFilterButton>
              </ItemCardTypeFilterContainer>

              {/* 카테고리별 필터 - 포인트 탭과 같은 스타일 */}
              <ItemCardCategoryFilterContainer>
                <ItemCardCategoryFilterButton 
                  $isActive={itemCardCategoryFilter === 'all'} 
                  onClick={() => setItemCardCategoryFilter('all')}
                >
                  <span className="full-text">전체 카테고리</span>
                  <span className="short-text">전체</span>
                </ItemCardCategoryFilterButton>
                <ItemCardCategoryFilterButton 
                  $isActive={itemCardCategoryFilter === 'levelup'} 
                  onClick={() => setItemCardCategoryFilter('levelup')}
                >
                  <span className="full-text">레벨업 카드</span>
                  <span className="short-text">레벨업</span>
                </ItemCardCategoryFilterButton>
                <ItemCardCategoryFilterButton 
                  $isActive={itemCardCategoryFilter === 'assignment'} 
                  onClick={() => setItemCardCategoryFilter('assignment')}
                >
                  <span className="full-text">과제보상 카드</span>
                  <span className="short-text">과제<br/>보상</span>
                </ItemCardCategoryFilterButton>
                <ItemCardCategoryFilterButton 
                  $isActive={itemCardCategoryFilter === 'store'} 
                  onClick={() => setItemCardCategoryFilter('store')}
                >
                  <span className="full-text">스토어 카드</span>
                  <span className="short-text">스토어</span>
                </ItemCardCategoryFilterButton>
              </ItemCardCategoryFilterContainer>
              
              <ItemCardGrid>
                {studentItemCards && studentItemCards.length > 0 ? (
                  [...studentItemCards]
                    // 사용 완료 카드를 마지막으로 정렬
                    .sort((a, b) => {
                      const aExhausted = a.type === 'active' && (a.remainingUseCount || 0) === 0 ? 1 : 0;
                      const bExhausted = b.type === 'active' && (b.remainingUseCount || 0) === 0 ? 1 : 0;
                      return aExhausted - bExhausted;
                    })
                    .filter(itemCard => {
                      // 잠금된 카드는 표시하지 않음
                      if (itemCard.isLocked === true) return false;

                      // 타입 필터링
                      let typeMatch = true;
                      if (itemCardFilter === 'all') typeMatch = true;
                      else typeMatch = itemCard.type === itemCardFilter;

                      // 카테고리 필터링
                      let categoryMatch = true;
                      if (itemCardCategoryFilter === 'all') categoryMatch = true;
                      else categoryMatch = itemCard.category === itemCardCategoryFilter;

                      return typeMatch && categoryMatch;
                    })
                    .map((itemCard, index) => {
                      const isLocked = itemCard.isLocked === true;
                      return (
                    <ItemCardBlock
                      key={itemCard.instanceId || index}
                      onClick={() => {
                        if (isLocked) return; // 잠금된 카드는 클릭 불가
                        if (itemCard.type === 'active') handleActiveCardClick(itemCard);
                      }}
                      $isActive={!isLocked && itemCard.type === 'active' && (itemCard.remainingUseCount || 0) > 0}
                      $isExhausted={!isLocked && itemCard.type === 'active' && (itemCard.remainingUseCount || 0) === 0}
                      $isLocked={isLocked}
                    >
                      {/* 카테고리 표시자 - 카드 이미지 오른쪽 상단 바깥쪽 */}
                      <CategoryBadge $category={itemCard.category}>
                        {itemCard.category === 'store' ? 'S' :
                         itemCard.category === 'assignment' ? 'A' :
                         itemCard.category === 'levelup' ? 'L' : ''}
                      </CategoryBadge>
                      <CardDeleteButton
                        $isLocked={isLocked}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCardClick(e, itemCard);
                        }}
                        disabled={isLocked}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </CardDeleteButton>
                      <ItemCardIconArea>
                        <PlaceholderCardIcon>
                          {itemCardDetails[itemCard.itemCardId]?.cardImage === 'default' ? (
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                              <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                              <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                            </svg>
                          ) : itemCardDetails[itemCard.itemCardId]?.cardImage && /^\d+$/.test(itemCardDetails[itemCard.itemCardId].cardImage) ? (
                            <img 
                              src={`/CardIcon/IconBasic/IconBasic-${itemCardDetails[itemCard.itemCardId].cardImage}.png`} 
                              alt={itemCardDetails[itemCard.itemCardId]?.name || '아이템 카드'}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : itemCardDetails[itemCard.itemCardId]?.cardImage ? (
                            <img 
                              src={withImageVersion(
                                itemCardDetails[itemCard.itemCardId].cardImage,
                                itemCardDetails[itemCard.itemCardId]?.updatedAt
                              )}
                              alt={itemCardDetails[itemCard.itemCardId]?.name || '아이템 카드'}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : itemCardDetails[itemCard.itemCardId]?.icon ? (
                            <div dangerouslySetInnerHTML={{ __html: itemCardDetails[itemCard.itemCardId].icon }} />
                          ) : (
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                              <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                              <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                            </svg>
                          )}
                        </PlaceholderCardIcon>
                        {/* 레벨업 아이템 카드인 경우 레벨 표시 */}
                        {(itemCard.category === 'levelup' || itemCard.category === 'Levelup') && 
                         typeof itemCard.acquiredLevel === 'number' && (
                          <LevelBadge $levelColor={getLevelColor(itemCard.acquiredLevel)} $level={itemCard.acquiredLevel}>
                            레벨{itemCard.acquiredLevel}
                          </LevelBadge>
                        )}
                      </ItemCardIconArea>
                      <ItemCardName>
                        {itemCardDetails[itemCard.itemCardId]?.name || `아이템 카드 #${itemCard.itemCardId || 'Unknown'}`}
                      </ItemCardName>
                      <ItemCardDescription>
                        {itemCardDetails[itemCard.itemCardId]?.memo || '아이템 카드 설명이 없습니다.'}
                      </ItemCardDescription>
                      {itemCard.isLocked === true && (
                        <ItemCardLockedBadge>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          <span>잠금</span>
                        </ItemCardLockedBadge>
                      )}
                      {!itemCard.isLocked && itemCard.type === 'active' && (
                        <ItemCardUseCount>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="#e74c3c"/>
                          </svg>
                          <span>× {itemCard.remainingUseCount || 0}</span>
                        </ItemCardUseCount>
                      )}
                    </ItemCardBlock>
                  );
                  })
                ) : (
                  <EmptyCardMessage>
                    {(() => {
                      if (itemCardFilter === 'all' && itemCardCategoryFilter === 'all') {
                        return '보유한 아이템 카드가 없습니다.';
                      } else if (itemCardFilter === 'passive') {
                        return '보유한 패시브 카드가 없습니다.';
                      } else if (itemCardFilter === 'active') {
                        return '보유한 액티브 카드가 없습니다.';
                      } else if (itemCardCategoryFilter === 'levelup') {
                        return '보유한 레벨업 카드가 없습니다.';
                      } else if (itemCardCategoryFilter === 'assignment') {
                        return '보유한 과제보상 카드가 없습니다.';
                      } else if (itemCardCategoryFilter === 'store') {
                        return '보유한 스토어 카드가 없습니다.';
                      } else {
                        return '보유한 아이템 카드가 없습니다.';
                      }
                    })()}
                  </EmptyCardMessage>
                )}
              </ItemCardGrid>
            </ItemCardTabContent>
          </>
        )}
      </ModalContent>

      {/* 저장 확인 모달 */}
      {showSaveConfirmModal && (
        <>
          <DeleteConfirmOverlay onClick={cancelSave} />
          <DeleteConfirmContainer onClick={(e) => e.stopPropagation()}>
            <DeleteConfirmContent>
              <DeleteConfirmIcon>!</DeleteConfirmIcon>
              <DeleteConfirmTitle>포인트 기록을 삭제하시겠습니까?</DeleteConfirmTitle>
              <DeleteConfirmDetails>
                삭제될 항목: {deletedPointIds.length}개
              </DeleteConfirmDetails>
            </DeleteConfirmContent>
            <DeleteConfirmFooter>
              <CancelDeleteButton data-variant="modal-secondary" onClick={cancelSave}>
                취소
              </CancelDeleteButton>
              <ConfirmDeleteButton onClick={confirmSave} disabled={isDeleting}>
                {isDeleting ? '삭제 중...' : '삭제'}
              </ConfirmDeleteButton>
            </DeleteConfirmFooter>
          </DeleteConfirmContainer>
        </>
      )}

      {/* 코인 삭제 확인 모달 */}
      {showCoinDeleteConfirmModal && (
        <>
          <DeleteConfirmOverlay onClick={cancelCoinDelete} />
          <DeleteConfirmContainer onClick={(e) => e.stopPropagation()}>
            <DeleteConfirmContent>
              <DeleteConfirmIcon>!</DeleteConfirmIcon>
              <DeleteConfirmTitle>코인 기록을 삭제하시겠습니까?</DeleteConfirmTitle>
              <DeleteConfirmDetails>
                삭제될 항목: {deletedCoinIds.length}개
              </DeleteConfirmDetails>
            </DeleteConfirmContent>
            <DeleteConfirmFooter>
              <CancelDeleteButton data-variant="modal-secondary" onClick={cancelCoinDelete}>
                취소
              </CancelDeleteButton>
              <ConfirmDeleteButton onClick={handleDeleteSelectedCoinHistory} disabled={isDeletingCoins}>
                {isDeletingCoins ? '삭제 중...' : '삭제'}
              </ConfirmDeleteButton>
            </DeleteConfirmFooter>
          </DeleteConfirmContainer>
        </>
      )}

      {/* 코인 추가 모달 */}
      {showCoinAddModal && (
        <>
          <DeleteConfirmOverlay onClick={() => setShowCoinAddModal(false)} />
          <DeleteConfirmContainer onClick={(e) => e.stopPropagation()}>
            <CoinModalContent>
              <CoinModalTitle>코인 추가</CoinModalTitle>
              <CoinModalField>
                <CoinModalLabel>획득 코인</CoinModalLabel>
                <CoinAmountContainer>
                  <CoinButton onClick={handleDecrementCoin} type="button">
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </CoinButton>
                  {isEditingCoinAmount ? (
                    <CoinAmountInput
                      type="text"
                      value={tempCoinInput}
                      onChange={handleCoinInputChange}
                      onBlur={handleCoinInputBlur}
                      onKeyDown={handleCoinInputKeyDown}
                      onFocus={(e) => e.target.select()}
                      autoFocus
                      $isCoinUse={false}
                    />
                  ) : (
                    <CoinAmountDisplay onClick={handleCoinAmountClick} $isCoinUse={false}>
                      {coinAmount}
                    </CoinAmountDisplay>
                  )}
                  <CoinButton onClick={handleIncrementCoin} type="button">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </CoinButton>
                </CoinAmountContainer>
              </CoinModalField>
              <CoinModalField>
                <CoinModalLabel>획득 메모</CoinModalLabel>
                <CoinModalInput
                  type="text"
                  placeholder="메모 입력"
                  value={coinMemo}
                  onChange={(e) => setCoinMemo(e.target.value)}
                />
              </CoinModalField>
            </CoinModalContent>
            <DeleteConfirmFooter>
              <CancelDeleteButton onClick={() => setShowCoinAddModal(false)}>
                닫기
              </CancelDeleteButton>
              <ConfirmDeleteButton onClick={handleCoinAdd}>
                저장
              </ConfirmDeleteButton>
            </DeleteConfirmFooter>
          </DeleteConfirmContainer>
        </>
      )}

      {/* 코인 사용 모달 */}
      {showCoinUseModal && (
        <>
          <DeleteConfirmOverlay onClick={() => setShowCoinUseModal(false)} />
          <DeleteConfirmContainer onClick={(e) => e.stopPropagation()}>
            <CoinModalContent>
              <CoinModalTitle>코인 사용</CoinModalTitle>
              <CoinModalField>
                <CoinModalLabel>사용 코인</CoinModalLabel>
                <CoinAmountContainer>
                  <CoinButton onClick={handleDecrementCoin} type="button">
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </CoinButton>
                  {isEditingCoinAmount ? (
                    <CoinAmountInput
                      type="text"
                      value={`-${tempCoinInput}`}
                      onChange={handleCoinInputChange}
                      onBlur={handleCoinInputBlur}
                      onKeyDown={handleCoinInputKeyDown}
                      onFocus={(e) => e.target.select()}
                      autoFocus
                      $isCoinUse={true}
                    />
                  ) : (
                    <CoinAmountDisplay onClick={handleCoinAmountClick} $isCoinUse={true}>
                      -{coinAmount}
                    </CoinAmountDisplay>
                  )}
                  <CoinButton onClick={handleIncrementCoin} type="button">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </CoinButton>
                </CoinAmountContainer>
              </CoinModalField>
              <CoinModalField>
                <CoinModalLabel>사용 메모</CoinModalLabel>
                <CoinModalInput
                  type="text"
                  placeholder="메모 입력"
                  value={coinMemo}
                  onChange={(e) => setCoinMemo(e.target.value)}
                />
              </CoinModalField>
            </CoinModalContent>
            <DeleteConfirmFooter>
              <CancelDeleteButton onClick={() => setShowCoinUseModal(false)}>
                닫기
              </CancelDeleteButton>
              <ConfirmDeleteButton onClick={handleCoinUse}>
                저장
              </ConfirmDeleteButton>
            </DeleteConfirmFooter>
          </DeleteConfirmContainer>
        </>
      )}

      {/* 아이템 카드 삭제 확인 모달 */}
      {showDeleteCardModal && selectedCardForDelete && (
        <>
          <DeleteCardModalOverlay onClick={() => setShowDeleteCardModal(false)} />
          <DeleteCardModalContainer onClick={(e) => e.stopPropagation()}>
            <DeleteCardModalHeader>
              <DeleteCardModalTitle>아이템 카드 삭제</DeleteCardModalTitle>
              <DeleteCardModalCloseButton onClick={() => setShowDeleteCardModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </DeleteCardModalCloseButton>
            </DeleteCardModalHeader>
            <DeleteCardModalBody>
              <DeleteCardInfo>
                <DeleteCardName>
                  {itemCardDetails[selectedCardForDelete.itemCardId]?.name || '아이템 카드'}
                </DeleteCardName>
                <DeleteCardMessage>
                  카드를 삭제하시겠습니까?
                </DeleteCardMessage>
                <DeleteCardWarning>
                  이 작업은 되돌릴 수 없습니다.
                </DeleteCardWarning>
              </DeleteCardInfo>
              <DeleteCardModalButtons>
                <DeleteCardCancelButton onClick={() => setShowDeleteCardModal(false)}>
                  취소
                </DeleteCardCancelButton>
                <DeleteCardConfirmButton 
                  onClick={handleDeleteCard}
                  disabled={isDeletingCard}
                >
                  {isDeletingCard ? '삭제 중...' : '삭제'}
                </DeleteCardConfirmButton>
              </DeleteCardModalButtons>
            </DeleteCardModalBody>
          </DeleteCardModalContainer>
        </>
      )}

      {/* 액티브 카드 사용 확인 모달 */}
      {showUseCardModal && selectedCardForUse && (
        <>
          <UseCardModalOverlay onClick={(e) => {
            e.stopPropagation(); // 이벤트 전파 중지하여 부모 모달이 닫히지 않도록
            setShowUseCardModal(false);
          }} />
          <UseCardModalContainer onClick={(e) => e.stopPropagation()}>
            <UseCardModalHeader>
              <UseCardModalTitle>
                {itemCardDetails[selectedCardForUse.itemCardId]?.name || '아이템 카드'}
              </UseCardModalTitle>
              <UseCardModalCloseButton onClick={() => setShowUseCardModal(false)}>×</UseCardModalCloseButton>
            </UseCardModalHeader>
            <UseCardModalContent>
              <UseCardModalMessage>
                {itemCardDetails[selectedCardForUse.itemCardId]?.name || '아이템'} 카드 사용!
              </UseCardModalMessage>
              
              {/* 사용 내역 */}
              {selectedCardForUse.usageHistory && selectedCardForUse.usageHistory.length > 0 && (
                <UseCardHistorySection>
                  <UseCardHistoryTitle>사용 내역</UseCardHistoryTitle>
                  <UseCardHistoryList>
                    {selectedCardForUse.usageHistory.map((history: any, idx: number) => {
                      const usedDate = history.usedAt?.toDate ? history.usedAt.toDate() : new Date(history.usedAt);
                      const dateStr = `${usedDate.getFullYear()}.${String(usedDate.getMonth() + 1).padStart(2, '0')}.${String(usedDate.getDate()).padStart(2, '0')}`;
                      const timeStr = `${String(usedDate.getHours()).padStart(2, '0')}:${String(usedDate.getMinutes()).padStart(2, '0')}`;
                      return (
                        <UseCardHistoryItem key={idx}>
                          <UseCardHistoryDate>{dateStr} {timeStr}</UseCardHistoryDate>
                          {history.memo && <UseCardHistoryMemo>{history.memo}</UseCardHistoryMemo>}
                        </UseCardHistoryItem>
                      );
                    })}
                  </UseCardHistoryList>
                </UseCardHistorySection>
              )}
              
              {(selectedCardForUse.remainingUseCount || 0) > 0 ? (
                <UseCardInputSection>
                  <UseCardMemoInput
                    type="text"
                    placeholder="메모 입력 (선택, 최대 20자)"
                    value={useCardMemo}
                    onChange={(e) => setUseCardMemo(e.target.value.slice(0, 20))}
                    onFocus={(e) => {
                      // 기본값이 "mm.dd 사용" 형식일 때 클릭하면 초기화
                      const now = new Date();
                      const month = String(now.getMonth() + 1).padStart(2, '0');
                      const day = String(now.getDate()).padStart(2, '0');
                      const defaultValue = `${month}.${day} 사용`;
                      if (e.target.value === defaultValue) {
                        setUseCardMemo('');
                      }
                    }}
                    maxLength={20}
                  />
                  <UseCardMemoCounter>{useCardMemo.length}/20</UseCardMemoCounter>
                </UseCardInputSection>
              ) : (
                <UseCardExhaustedMessage>
                  모든 사용 횟수를 소진한 카드입니다.
                </UseCardExhaustedMessage>
              )}
            </UseCardModalContent>
            <UseCardModalFooter>
              <UseCardCancelButton onClick={() => setShowUseCardModal(false)}>
                {(selectedCardForUse.remainingUseCount || 0) > 0 ? '취소' : '닫기'}
              </UseCardCancelButton>
              {(selectedCardForUse.remainingUseCount || 0) > 0 && (
                <UseCardConfirmButton onClick={handleUseActiveCard} disabled={isUsingCard}>
                  {isUsingCard ? '사용 중...' : '사용'}
                </UseCardConfirmButton>
              )}
            </UseCardModalFooter>
          </UseCardModalContainer>
        </>
      )}

      {/* 아이템 카드 사용 토스트 */}
      {useCardToast.show && (
        <UseCardToast onClick={(e) => e.stopPropagation()}>
          <UseCardToastContent onClick={(e) => e.stopPropagation()}>
            <UseCardToastImage>
              {useCardToast.cardImage === 'default' ? (
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                  <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                  <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                </svg>
              ) : useCardToast.cardImage && /^\d+$/.test(String(useCardToast.cardImage)) ? (
                <img src={`/CardIcon/IconBasic/IconBasic-${useCardToast.cardImage}.png`} alt="카드" />
              ) : useCardToast.cardImage ? (
                <img src={useCardToast.cardImage} alt="카드" />
              ) : (
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="6" width="12" height="16" rx="2" fill="var(--color-surface-highlight)" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                  <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                  <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                </svg>
              )}
            </UseCardToastImage>
            <UseCardToastMessageWrap>
              <UseCardToastMessage>{useCardToast.itemName}</UseCardToastMessage>
            </UseCardToastMessageWrap>
            <UseCardToastLabel>아이템 카드 사용</UseCardToastLabel>
            <UseCardToastRemaining>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="#e74c3c"/>
              </svg>
              <span>× {useCardToast.remainingCount}</span>
              <span className="remaining-text">남았습니다</span>
            </UseCardToastRemaining>
            <UseCardToastConfirmButton
              type="button"
              onClick={() => {
                if (useCardToastTimeoutRef.current) {
                  clearTimeout(useCardToastTimeoutRef.current);
                  useCardToastTimeoutRef.current = null;
                }
                setUseCardToast({ show: false, itemName: '', cardImage: 'default', remainingCount: 0 });
              }}
            >
              확인
            </UseCardToastConfirmButton>
          </UseCardToastContent>
        </UseCardToast>
      )}

      <StoreModal
        isOpen={showStoreModal}
        onClose={() => setShowStoreModal(false)}
        userId={userId}
        classId={classId}
        studentId={student?.id || ''}
        memberGroup={memberGroup || 'A'}
        currentCoins={currentCoins}
        userType={user.userType}
        onPurchaseSuccess={() => {
          loadAllData({ force: true, reason: 'store-purchase', silent: true });
          onDataChange?.();
        }}
      />
    </ModalOverlay>
  );
};

export default StudentPointDetailModal;

// 스타일 컴포넌트들
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 80px 20px;
  overflow-y: auto;
  min-height: 100vh;
  
  @media (max-width: 600px) {
    padding: 60px 20px;
  }
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  overflow: visible;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderCenterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StoreButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-surface);
  border: 1px solid var(--store-icon-border-color);
  color: var(--color-accent-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    border-color: rgba(var(--color-accent-rgb), 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StudentNumber = styled.span`
  font-size: 16px;
    font-weight: 600;
  color: var(--color-text-gray);
`;

const StudentNameText = styled.span`
  font-size: 18px;
  font-weight: 700;
    color: var(--color-text-primary);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PointBadge = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color-accent-strong);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
`;

const CoinIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardIcon = styled.div<{ $exhausted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$exhausted ? 0.5 : 1};

  ${props => !props.$exhausted && `
    /* 다크모드: 보유 아이템 카드 스택의 가장 뒤 카드 흰색 */
    :root:not([data-theme='light']) & svg rect:first-of-type {
      fill: #ffffff;
    }
  `}

  ${props => props.$exhausted && `
    /* 다크모드: 사용완료 카드 스택 내부를 모두 #222222 */
    :root:not([data-theme='light']) & svg rect {
      fill: #222222;
    }
  `}
`;

const DeleteButton = styled.button<{ $hasChanges: boolean; $isDeleted?: boolean }>`
  width: 100%;
  height: 100%;
  background: ${props => props.$hasChanges ? 'var(--color-accent-strong)' : props.$isDeleted ? '#6c757d' : 'var(--color-border)'};
  color: ${props => props.$hasChanges ? 'white' : 'var(--color-text-muted)'};
  border: none;
  border-radius: 0;
  cursor: ${props => props.$hasChanges ? 'pointer' : 'not-allowed'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;

  /* 코인 탭: 삭제 예정(회색)일 때 라이트 모드에서 연한 회색 배경 + 아이콘 대비 */
  ${props => props.$isDeleted && `
    :root[data-theme='light'] & {
      background: #b0b0b0;
      color: #555555;
    }
  `}

  &:hover {
    background: ${props => props.$hasChanges ? 'rgba(var(--color-accent-rgb), 0.8)' : props.$isDeleted ? '#6c757d' : 'var(--color-border)'};
  }
  ${props => props.$isDeleted && `
    :root[data-theme='light'] &:hover {
      background: #a0a0a0;
    }
  `}

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: var(--color-border);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text-gray);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--color-text-primary);
  }
`;

// 새로운 포인트 버튼 스타일
const PointsButtonContainer = styled.div`
  display: flex;
  background: var(--color-border);
  border-radius: 12px;
  padding: 4px;
  position: relative;
  width: 100%;
  margin-top: 24px;
  margin-bottom: 8px;
  margin-left: 24px;
  margin-right: 24px;
  width: calc(100% - 48px);
`;

const PointsSlider = styled.div<{ $position: number }>`
  position: absolute;
  top: 4px;
  left: ${props => `${props.$position * 33.333}%`};
  width: calc(33.333% - 8px);
  height: calc(100% - 8px);
  background: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: left 0.3s ease;
  z-index: 1;
  margin: 0 4px;
`;

const PointsButton = styled.button<{ $isActive: boolean }>`
  position: relative;
  z-index: 2;
  background: transparent;
  border: none;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s ease;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    color: var(--color-text-primary);
  }

  @media (max-width: 600px) {
    flex-direction: column;
    justify-content: center;
    white-space: normal;
    padding: 8px 16px;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  .full-text {
    display: inline;
  }
  
  .short-text {
    display: none;
  }

  @media (max-width: 600px) {
    padding: 10px 12px;
    font-size: 11px;
    
    .full-text {
      display: none;
    }
    
    .short-text {
      display: inline;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 10px;
  }
`;

const PointsButtonLeft = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
  flex: 1;
  
  .full-text {
    display: inline !important;
    
    @media (max-width: 480px) {
      display: none !important;
    }
  }
  
  .short-text {
    display: none !important;
    
    @media (max-width: 480px) {
      display: inline !important;
    }
  }
`;

const PointsButtonRight = styled.div<{ $isActive: boolean }>`
  font-weight: 700;
  color: var(--color-text-primary);
  white-space: nowrap;
  font-size: 12px;
  flex-shrink: 0;
  
  @media (max-width: 600px) {
    font-size: 11px;
  }
`;

const FilterButtonLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 600px) {
    margin-bottom: 4px;
  }
  
  .full-text {
    @media (max-width: 480px) {
      display: none;
    }
  }
  
  .short-text {
    display: none;
    
    @media (max-width: 480px) {
      display: inline;
    }
  }
`;

const FilterButtonRight = styled.div`
  font-weight: 700;
  color: var(--color-text-primary);
  white-space: nowrap;
  font-size: 12px;
  
  @media (max-width: 600px) {
    font-size: 11px;
  }
`;

const TabContent = styled.div`
  padding: 24px;
  /* 개별 스크롤 제거 - 모달 전체 스크롤 사용 */
`;

const AssignmentTab = styled.div`
  // 과제 포인트 탭 스타일
`;

const PositiveTab = styled.div`
  // 긍정행동 포인트 탭 스타일
`;

const NegativeTab = styled.div`
  // 행동체크 포인트 탭 스타일
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  align-items: center;
  width: 100%;
  overflow: hidden;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &:focus {
    border-color: rgba(var(--color-accent-rgb), 0.5);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const SearchButton = styled.button`
  padding: 8px 12px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.8);
  }
`;

const PointDeleteButton = styled.button<{ $hasChanges: boolean }>`
  padding: 8px 12px;
  background: ${props => props.$hasChanges ? 'var(--color-accent-strong)' : 'var(--color-save-button-disabled-bg)'};
  color: ${props => props.$hasChanges ? 'white' : 'var(--color-save-button-disabled-text)'};
  border: none;
  border-radius: 6px;
  cursor: ${props => props.$hasChanges ? 'pointer' : 'not-allowed'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${props => props.$hasChanges ? 'var(--color-accent)' : 'var(--color-save-button-disabled-bg)'};
    transform: ${props => props.$hasChanges ? 'translateY(-1px)' : 'none'};
    box-shadow: ${props => props.$hasChanges ? '0 4px 8px rgba(0, 0, 0, 0.15)' : 'none'};
  }

  &:active:not(:disabled) {
    transform: ${props => props.$hasChanges ? 'translateY(0)' : 'none'};
    box-shadow: ${props => props.$hasChanges ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
  }

  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const FilterButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: ${props => props.$isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border-radius: 8px;
  font-size: 12px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  overflow: hidden;
  white-space: normal;
  word-break: keep-all;
  line-height: 1.2;
  min-height: 32px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 600px) {
    flex-direction: column;
    justify-content: center;
    min-height: 42px;
  }

  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  span {
    white-space: nowrap;
  }

  span.group {
    display: inline-block;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    padding: 5px 8px;
    font-size: 11px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const AssignmentsTable = styled.div`
  width: 100%;
  margin-top: 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 60px 200px 80px auto;
  background: var(--color-panel);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 600px) {
    grid-template-columns: 50px 60px 1fr 70px;
    grid-template-rows: auto auto;
  }
`;

const HeaderCell = styled.div<{ columnIndex?: number }>`
  padding: 8px 4px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  border-right: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 600px) {
    padding: 8px 12px;
    font-size: 12px;
    
    &:nth-child(1) { grid-row: 1; grid-column: 1; } /* 타입 */
    &:nth-child(2) { grid-row: 1; grid-column: 2; } /* 마감일 */
    &:nth-child(3) { grid-row: 1; grid-column: 3; } /* 과제명 */
    &:nth-child(4) { 
      grid-row: 1; 
      grid-column: 4; 
      border-right: none; /* 포인트 열 오른쪽 테두리 제거 */
    } /* 포인트 */
    &:nth-child(5) { 
      grid-row: 2; 
      grid-column: 1 / 5; 
      border-top: 1px solid var(--color-border);
      border-right: none;
    } /* 메모 - 2행 전체 */
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 50px 60px 200px 80px auto;
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
  transition: background-color 0.2s ease;
  min-height: 48px;
  align-items: center;

  @media (max-width: 600px) {
    grid-template-columns: 50px 60px 1fr 70px;
    grid-template-rows: auto auto;
  }

  &:hover {
    background: var(--color-panel);
  }
`;

const TableCell = styled.div<{ columnIndex?: number }>`
  padding: ${props =>
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? '8px 4px' : '8px 4px'};
  text-align: ${props =>
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? 'center' : 'left'};
  font-size: 14px;
  color: var(--color-text-primary);
  border-right: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: ${props =>
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? 'center' : 'flex-start'};
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 600px) {
    padding: 8px 12px;
    font-size: 12px;
    
    &:nth-child(1) { grid-row: 1; grid-column: 1; } /* 타입 */
    &:nth-child(2) { grid-row: 1; grid-column: 2; } /* 마감일 */
    &:nth-child(3) { grid-row: 1; grid-column: 3; } /* 과제명 */
    &:nth-child(4) { 
      grid-row: 1; 
      grid-column: 4; 
      border-right: none; /* 포인트 열 오른쪽 테두리 제거 */
    } /* 포인트 */
    &:nth-child(5) { 
      grid-row: 2; 
      grid-column: 1 / 5; 
      border-top: 1px solid var(--color-border);
      border-right: none;
    } /* 메모 - 2행 전체 */
  }
`;

const TypeBadge = styled.span<{ type: string }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  color: white;
  
  ${props => props.type === 'general' ? `
    background: #6c757d; /* 일반과제 - 기본 회색 */
  ` : props.type === 'project' ? `
    background: var(--color-accent-strong); /* 프로젝트 - 메인 청록색 */
  ` : `
    background: #ff6b6b; /* 루틴과제 - 루틴과제 고유 카테고리 색상 */
  `}
`;

const PointsText = styled.span<{ points: number; status?: string }>`
  font-weight: bold;
  color: ${props => {
    if (props.status === 'pending') return 'var(--status-pending)'; // 미확인 - 핑크색
    if (props.status === 'confirmed') return 'var(--reward-point-text-color)'; // 확인 - 일반모드: 초록색, 다크모드: 흰색
    if (props.status === 'delayed') return '#A5D6A7'; // 지연확인 - 연한 녹색
    if (props.status === 'exempted') return '#FFD54F'; // 예외 - 연한 노랑/주황
    if (props.status === 'excluded') return '#6c757d'; // 제외 - 회색
    return props.points === 0 ? 'var(--status-pending)' : 'var(--reward-point-text-color)'; // 기본값 (0점이면 미확인 색상, 아니면 일반모드: 초록색, 다크모드: 흰색)
  }};
  font-size: 14px;
`;

const RewardPointsText = styled.span<{ $digitCount?: number }>`
  font-weight: bold;
  color: var(--reward-point-text-color);
  font-size: ${props => {
    if (!props.$digitCount) return '14px';
    if (props.$digitCount <= 3) return '14px';
    if (props.$digitCount === 4) return '12px';
    return '10px';
  }};
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;


const EditableTableCell = styled.div<{ columnIndex?: number; $isEditing?: boolean }>`
  position: relative;
  padding: ${props =>
    props.columnIndex === 2 ? '8px 12px' : '8px 4px'}; // 코인 테이블과 동일한 패딩
  text-align: ${props => 
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? 'center' : 'left'};
  font-size: 14px;
  color: var(--color-text-primary);
  border-right: 1px solid var(--color-border);
  display: ${props => props.columnIndex === 2 ? 'block' : 'flex'};
  align-items: ${props => props.columnIndex === 2 ? 'flex-start' : 'center'};
  justify-content: ${props => 
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? 'center' : 'flex-start'};
  cursor: ${props => props.$isEditing ? 'pointer' : 'default'};
  word-wrap: break-word;
  word-break: break-word;
  white-space: ${props => props.columnIndex === 2 ? 'normal' : 'nowrap'};
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 600px) {
    padding: 6px 8px;
    font-size: 12px;
    
    &:nth-child(1) { grid-row: 1; grid-column: 1; } /* 타입 */
    &:nth-child(2) { grid-row: 1; grid-column: 2; } /* 날짜 */
    &:nth-child(3) { grid-row: 1; grid-column: 3; } /* 메모 */
    &:nth-child(4) { 
      grid-row: 1; 
      grid-column: 4; 
      border-right: none; /* 포인트 열 오른쪽 테두리 제거 */
    } /* 포인트 */
  }
`;

const RewardTable = styled.div`
  width: 100%;
  margin-top: 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
`;

const RewardTableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 60px 1fr 80px;
  background: var(--color-panel);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 600px) {
    grid-template-columns: 45px 55px 1fr 75px;
    grid-template-rows: auto auto;
  }
`;

const RewardHeaderCell = styled.div<{ columnIndex?: number }>`
  padding: 8px 4px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  border-right: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 600px) {
    padding: 6px 8px;
    font-size: 12px;
    
    &:nth-child(1) { grid-row: 1; grid-column: 1; } /* 타입 */
    &:nth-child(2) { grid-row: 1; grid-column: 2; } /* 날짜 */
    &:nth-child(3) { grid-row: 1; grid-column: 3; } /* 메모 */
    &:nth-child(4) { 
      grid-row: 1; 
      grid-column: 4; 
      border-right: none; /* 포인트 열 오른쪽 테두리 제거 */
    } /* 포인트 */
  }
`;

const RewardTableRow = styled.div<{ $isDeleted?: boolean; $isEmptyState?: boolean }>`
  display: grid;
  grid-template-columns: 50px 60px 1fr 80px;
  border-top: 1px solid var(--color-border);
  background: ${props => props.$isDeleted ? 'var(--color-border)' : 'var(--color-surface)'};
  transition: all 0.2s ease;
  position: relative;
  min-height: 48px;
  align-items: center;
  
  ${props => props.$isDeleted && `
    filter: grayscale(100%);
  `}
  
  @media (max-width: 600px) {
    grid-template-columns: 45px 55px 1fr 75px;
    grid-template-rows: auto auto;
    
    ${props => props.$isEmptyState && `
      grid-template-columns: 1fr;
      grid-template-rows: auto;
    `}
  }

  &:hover {
    background: ${props => props.$isDeleted ? 'var(--color-border)' : 'var(--color-surface-highlight)'};
  }
`;

const RewardTableCell = styled.div<{ columnIndex?: number; colSpan?: number }>`
  padding: ${props =>
    props.columnIndex === 2 ? '8px 12px' : '8px 4px'}; // 메모열 패딩을 코인 테이블과 동일하게
  text-align: ${props =>
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? 'center' : 'left'};
  font-size: 14px;
  color: var(--color-text-primary);
  border-right: 1px solid var(--color-border);
  display: ${props => props.columnIndex === 2 ? 'block' : 'flex'};
  align-items: ${props => props.columnIndex === 2 ? 'flex-start' : 'center'};
  justify-content: ${props =>
    props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3 ? 'center' : 'flex-start'};
  word-wrap: break-word;
  word-break: break-word;
  white-space: ${props => props.columnIndex === 2 ? 'normal' : 'nowrap'};
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 600px) {
    padding: 6px 8px;
    font-size: 12px;
    
    &:nth-child(1) { grid-row: 1; grid-column: 1; } /* 타입 */
    &:nth-child(2) { grid-row: 1; grid-column: 2; } /* 날짜 */
    &:nth-child(3) { grid-row: 1; grid-column: 3; } /* 메모 */
    &:nth-child(4) { 
      grid-row: 1; 
      grid-column: 4; 
      border-right: none; /* 포인트 열 오른쪽 테두리 제거 */
    } /* 포인트 */
  }
  
  ${props => props.colSpan && `
    grid-column: 1 / -1;
    text-align: center;
    justify-content: center;
    
    @media (max-width: 600px) {
      grid-column: 1;
      grid-row: 1;
      text-align: center;
      justify-content: center;
      padding: 20px 12px;
      font-size: 14px;
      border-right: none;
    }
  `}
`;


// 포인트 삭제 확인 모달 스타일
const DeleteConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1005;
`;

const DeleteConfirmContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 1006;
  width: 90vw;
  max-width: 400px;
  overflow: hidden;
  
  @media (max-width: 480px) {
    width: 95vw;
  }
`;

const DeleteConfirmContent = styled.div`
  padding: 32px 24px 24px;
  text-align: center;
`;

const DeleteConfirmIcon = styled.div`
  width: 60px;
  height: 60px;
  background: var(--status-exempted);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin: 0 auto 20px;
`;

const DeleteConfirmTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const DeleteConfirmDetails = styled.p`
  margin: 8px 0 0 0;
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
`;

const DeleteConfirmFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
`;

const CancelDeleteButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-accent-text);
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ConfirmDeleteButton = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-panel)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: var(--color-accent);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 메인 탭 스타일 (과제 페이지와 동일)
const MainTabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 0;
  background: var(--color-surface);
  overflow: hidden;
`;

const MainTabButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.$isActive ? 'white' : 'var(--color-text-secondary)'};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-right: 1px solid var(--color-border);
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
    transform: ${props => props.$isActive ? 'none' : 'translateY(-1px)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }
  
  @media (max-width: 600px) {
    font-size: 0.9rem;
    padding: 14px 20px;
  }
  
  @media (max-width: 500px) {
    font-size: 0.85rem;
    padding: 12px 16px;
  }
  
  @media (max-width: 420px) {
    font-size: 0.8rem;
    padding: 10px 12px;
  }
`;

// 탭 내부 요약 정보 스타일
const ItemCardSummaryRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 48px;
  background: var(--color-panel);
  border-bottom: 1px solid var(--color-border);
  padding: 24px 0;
`;

const ItemCardSummaryItem = styled.div`
  padding: 0;
  margin-top: 0;
`;

const TabSummaryContainer = styled.div`
  background: var(--color-panel);
  border-bottom: 1px solid var(--color-border);
  padding: 24px 0;
  margin-top: 0;
`;

const TabSummaryTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6c757d;
  text-align: center;
  margin-bottom: 12px;
`;

const TabSummaryContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const TabSummaryValue = styled.div<{ $exhausted?: boolean }>`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.$exhausted ? '#999' : 'var(--color-text-primary)'};
`;

// 코인 탭 스타일
const CoinTabContent = styled.div`
  padding: 24px;
`;

const CoinSearchContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  align-items: center;
`;

const CoinSearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  min-width: 0;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.1);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const CoinSearchButton = styled.button`
  padding: 8px 12px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: 40px;
  min-height: 36px;
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.9);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  svg {
    display: block;
  }
`;

const CoinDeleteButton = styled.button<{ $hasChanges: boolean }>`
  padding: 8px 12px;
  background: ${props => props.$hasChanges ? 'var(--color-accent-strong)' : 'var(--color-save-button-disabled-bg)'};
  color: ${props => props.$hasChanges ? 'white' : 'var(--color-save-button-disabled-text)'};
  border: none;
  border-radius: 6px;
  cursor: ${props => props.$hasChanges ? 'pointer' : 'not-allowed'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  min-width: 50px;
  min-height: 36px;
  
  &:hover:not(:disabled) {
    background: ${props => props.$hasChanges ? 'var(--color-accent)' : 'var(--color-save-button-disabled-bg)'};
    transform: ${props => props.$hasChanges ? 'translateY(-1px)' : 'none'};
    box-shadow: ${props => props.$hasChanges ? '0 4px 8px rgba(0, 0, 0, 0.15)' : 'none'};
  }
  
  &:active:not(:disabled) {
    transform: ${props => props.$hasChanges ? 'translateY(0)' : 'none'};
    box-shadow: ${props => props.$hasChanges ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none'};
  }
  
  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CoinTable = styled.div`
  width: 100%;
  margin-top: 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  overflow: hidden;
`;

const CoinTableHeader = styled.div`
  display: grid;
  grid-template-columns: 50px 60px 1fr 80px;
  background: var(--color-panel);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 600px) {
    grid-template-columns: 45px 55px 1fr 75px;
  }
`;

const CoinHeaderCell = styled.div<{ columnIndex?: number }>`
  padding: 8px 4px;
  text-align: center; // 모든 헤더 중앙 정렬
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  border-right: 1px solid var(--color-border);
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center; // 모든 헤더 중앙 정렬

  &:last-child {
    border-right: none;
  }

  @media (max-width: 600px) {
    padding: 8px;
    font-size: 12px;
  }
`;

const CoinTableBody = styled.div``;

const CoinTableRow = styled.div<{ $isDeleted?: boolean; $isEmptyState?: boolean }>`
  display: grid;
  grid-template-columns: 50px 60px 1fr 80px;
  border-top: 1px solid var(--color-border);
  background: ${props => props.$isDeleted ? 'var(--color-border)' : 'var(--color-surface)'};
  transition: all 0.2s ease;
  position: relative;
  min-height: 48px;
  align-items: center;

  ${props => props.$isDeleted && `
    filter: grayscale(100%);
  `}

  ${props => props.$isEmptyState && `
    grid-template-columns: 1fr;
  `}

  @media (max-width: 600px) {
    grid-template-columns: 45px 55px 1fr 75px;

    ${props => props.$isEmptyState && `
      grid-template-columns: 1fr;
    `}
  }

  &:hover {
    background: ${props => props.$isDeleted ? 'var(--color-border)' : 'var(--color-surface-highlight)'};
  }
`;

const CoinCell = styled.div<{ columnIndex?: number; colSpan?: number; $coinValue?: number }>`
  position: relative;
  padding: ${props =>
    props.columnIndex === 2 ? '8px 12px' : '8px 4px'}; // 메모열 패딩 증가
  font-size: 14px;
  font-weight: ${props => props.columnIndex === 3 ? 700 : 400}; // 코인 값 볼드
  color: ${props => {
    // 획득 코인 열이고 음수인 경우 빨간색
    if (props.columnIndex === 3 && props.$coinValue && props.$coinValue < 0) {
      return '#ff6b6b';
    }
    return 'var(--color-text-primary)';
  }};
  border-right: 1px solid var(--color-border);
  text-align: ${props => {
    // 타입열(0), 날짜열(1), 획득코인열(3)은 가운데 정렬
    if (props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3) {
      return 'center';
    }
    // 메모열(2)은 왼쪽 정렬
    return 'left';
  }};
  display: ${props => props.columnIndex === 2 ? 'block' : 'flex'};
  align-items: ${props => props.columnIndex === 2 ? 'flex-start' : 'center'};
  justify-content: ${props => {
    if (props.columnIndex === 0 || props.columnIndex === 1 || props.columnIndex === 3) {
      return 'center';
    }
    return 'flex-start';
  }};
  word-wrap: break-word;
  word-break: break-word;
  white-space: ${props => props.columnIndex === 2 ? 'normal' : 'nowrap'};
  overflow: hidden;
  min-width: 0;
  max-width: 100%;

  &:last-child {
    border-right: none;
  }

  ${props => props.colSpan && `
    grid-column: 1 / -1;
    text-align: center;
    justify-content: center;
    border-right: none;
  `}

  @media (max-width: 600px) {
    padding: 6px 8px; // 다른 테이블과 동일한 모바일 패딩
    font-size: 12px;
  }
`;

const CoinTypeBadge = styled.span<{ $coinType: 'General' | 'Levelup' | 'Store' | 'Assignment' }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  color: white;

  ${props => {
    switch (props.$coinType) {
      case 'General':
        return 'background: #6c757d;'; /* 일반 코인 - 기본 회색 */
      case 'Levelup':
        return 'background: var(--color-accent-strong);'; /* 레벨업 코인 - 메인 청록색 */
      case 'Store':
        return 'background: #e67e22;'; /* 스토어 코인 - 주황색 */
      case 'Assignment':
        return 'background: #3498db;'; /* 과제 코인 - 파란색 */
      default:
        return 'background: #6c757d;';
    }
  }}
`;


// 아이템 카드 탭 스타일
const ItemCardTabContent = styled.div`
  padding: 24px;
`;

const ItemCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 180px);
  gap: 20px;
  justify-content: center;
  padding: 20px 0;

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 480px) {
    gap: 12px;
  }
`;

// 카드 삭제 버튼 (휴지통) - 카테고리 배지보다 위에 표시되어 클릭 가능하도록
const CardDeleteButton = styled.button<{ $isLocked?: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.$isLocked ? 'not-allowed' : 'pointer'};
  color: var(--color-text-muted);
  transition: all 0.2s ease;
  z-index: 50;
  opacity: 0;

  &:hover:not(:disabled) {
    background: var(--color-surface);
    border-color: #e74c3c;
    color: #e74c3c;
    box-shadow: 0 2px 8px rgba(231, 76, 60, 0.2);
  }

  &:disabled {
    opacity: 0.3 !important;
    cursor: not-allowed;
  }
`;

const ItemCardBlock = styled.div<{ $isActive?: boolean; $isExhausted?: boolean; $isLocked?: boolean }>`
  width: 180px;
  height: 300px;
  border: 2px solid ${props => {
    if (props.$isLocked) return '#999';
    if (props.$isExhausted) return 'var(--color-accent-strong)';
    if (props.$isActive) return 'var(--color-accent-strong)';
    return 'var(--color-accent-strong)';
  }};
  border-radius: 12px;
  background: ${props => {
    if (props.$isLocked) return 'var(--color-surface-highlight)';
    return props.$isExhausted ? 'var(--color-surface-highlight)' : 'var(--color-surface)';
  }};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  cursor: ${props => {
    if (props.$isLocked) return 'not-allowed';
    return (props.$isActive || props.$isExhausted) ? 'pointer' : 'default';
  }};
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;
  opacity: ${props => {
    if (props.$isLocked) return 0.5;
    return props.$isExhausted ? 0.6 : 1;
  }};
  filter: ${props => {
    if (props.$isLocked) return 'grayscale(100%)';
    return props.$isExhausted ? 'grayscale(100%)' : 'none';
  }};

  /* 다크 모드: 레벨업/스토어/과제보상 카드 테두리 동일 (#D3D3D3) */
  :root:not([data-theme='light']) & {
    border: 2px solid ${props => {
      if (props.$isLocked) return '#D3D3D3';
      if (props.$isExhausted) return '#D3D3D3';
      if (props.$isActive) return '#D3D3D3';
      return '#D3D3D3';
    }};
  }

  &:hover {
    transform: ${props => {
      if (props.$isLocked) return 'none';
      return (props.$isActive || props.$isExhausted) ? 'translateY(-4px)' : 'translateY(-2px)';
    }};
    box-shadow: ${props => {
      if (props.$isLocked) return 'none';
      if (props.$isExhausted) return '0 4px 12px rgba(0, 0, 0, 0.1)';
      if (props.$isActive) return '0 8px 20px rgba(var(--color-accent-rgb), 0.25)';
      return '0 4px 16px rgba(0, 0, 0, 0.15)';
    }};
    border-color: var(--color-accent-strong);
    
    ${CardDeleteButton} {
      opacity: 1; // 잠겨있는 카드도 삭제 버튼 표시
    }
  }
`;

// 카테고리 표시자 스타일 (휴지통 아이콘과 겹치는 위치, 삭제 버튼 아래)
const CategoryBadge = styled.div<{ $category?: string }>`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: white;
  border: 2px solid ${props => {
    switch (props.$category) {
      case 'store':
        return '#3498db'; // 파란색
      case 'assignment':
        return '#27ae60'; // 초록색
      case 'levelup':
        return '#f39c12'; // 주황색
      default:
        return '#95a5a6'; // 회색
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: ${props => {
    switch (props.$category) {
      case 'store':
        return '#3498db'; // 파란색
      case 'assignment':
        return '#27ae60'; // 초록색
      case 'levelup':
        return '#f39c12'; // 주황색
      default:
        return '#95a5a6'; // 회색
    }
  }};
  z-index: 10;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: opacity 0.2s ease;
  pointer-events: none;

  /* 카드 블록 호버 시 카테고리 배지 숨김 */
  ${ItemCardBlock}:hover & {
    opacity: 0;
  }
`;

// 레벨 색상 정의 (LevelSettingsModal과 동일)
const levelColors = [
  '#FC5555', // 레벨1, 11, 21... - 빨간색
  '#FF9933', // 레벨2, 12, 22... - 주황색
  '#CCCC00', // 레벨3, 13, 23... - 노란색
  '#669900', // 레벨4, 14, 24... - 연두색
  '#119473', // 레벨5, 15, 25... - 청록색
  '#0066CC', // 레벨6, 16, 26... - 파란색
  '#9900CC', // 레벨7, 17, 27... - 보라색
  '#920049', // 레벨8, 18, 28... - 진한 분홍색
  '#D60093'  // 레벨9, 19, 29... - 분홍색
];

// 레벨별 색상 가져오기 (10개 색상으로 순환)
const getLevelColor = (level: number): string => {
  // 레벨 0, 10, 20 등 (10의 배수)일 때는 메인테마 초록/흰색
  if (level % 10 === 0) {
    return 'var(--color-level-0)';
  }
  // 나머지 레벨은 1~9 색상 반복 (레벨 1→인덱스0, 레벨 2→인덱스1, ... 레벨 9→인덱스8)
  const colorIndex = (level % 10) - 1;
  return levelColors[colorIndex];
};

const ItemCardIconArea = styled.div`
  width: 100%;
  aspect-ratio: 1;
  position: relative;
  border-radius: 8px;
  background: var(--color-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 12px;
  border: 2px solid var(--color-accent-strong);

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PlaceholderCardIcon = styled.div`
  font-size: 48px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 레벨 배지 텍스트 색상 가져오기
const getLevelTextColor = (level: number): string => {
  // 레벨 0, 10, 20 등 (10의 배수)일 때는 다크모드에서 흰색 배경이므로 검정색 텍스트
  if (level % 10 === 0) {
    // 다크모드에서 흰색 배경일 때 검정색 텍스트, 라이트모드에서 초록색 배경일 때 흰색 텍스트
    return 'var(--color-level-0-text)';
  }
  // 다른 레벨은 항상 흰색
  return 'white';
};

// 레벨 표시 배지 (아이템 카드 이미지 상단 테두리에 표시)
const LevelBadge = styled.div<{ $levelColor: string; $level?: number }>`
  position: absolute;
  top: 4px;
  left: 4px;
  background: ${props => props.$levelColor};
  color: ${props => props.$level !== undefined ? getLevelTextColor(props.$level) : 'white'};
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  line-height: 1.2;
`;

const ItemCardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

// 카테고리 필터 컨테이너 - 포인트 탭과 같은 스타일
const ItemCardCategoryFilterContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 600px) {
    gap: 6px;
  }
`;

// 카테고리 필터 버튼 - 포인트 탭과 같은 스타일
const ItemCardCategoryFilterButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: ${props => props.$isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border-radius: 8px;
  font-size: 12px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;
  text-align: center;
  
  .full-text {
    display: inline;
    @media (max-width: 480px) {
      display: none;
    }
  }
  
  .short-text {
    display: none;
    @media (max-width: 480px) {
      display: inline;
    }
  }
  
  &:hover {
    background: ${props => props.$isActive ? 'var(--color-panel)' : 'var(--color-panel)'};
    border-color: ${props => props.$isActive ? 'var(--color-text-muted)' : 'var(--color-border)'};
  }
  
  @media (max-width: 600px) {
    font-size: 11px;
    padding: 5px 8px;
  }
`;

const ItemCardDescription = styled.div`
  font-size: 12px;
  color: #999;
  text-align: center;
  margin-bottom: 12px;
  line-height: 1.4;
  height: 32px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  width: 100%;
`;

const ItemCardLockedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(153, 153, 153, 0.2);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #999;
  margin-top: auto;
  
  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
  }
  
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ItemCardUseCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: auto;
  
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  span {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    line-height: 1;
  }
`;

const EmptyCardMessage = styled.div`
  grid-column: 1 / -1;
  padding: 60px 20px;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
`;


const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: var(--color-text-gray);
  margin-top: 20px;
`;

// 코인 버튼 스타일
const CoinButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  align-items: center;
  justify-content: center;
`;

const CoinAddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--color-surface);
  color: var(--color-accent-text);
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CoinUseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--color-surface);
  color: #ff6b6b;
  border: 2px solid #ff6b6b;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 코인 모달 스타일
const CoinModalContent = styled.div`
  padding: 24px;
`;

const CoinModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 10px 0;
  text-align: center;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const CoinModalField = styled.div`
  margin-bottom: 16px;
`;

const CoinModalLabel = styled.label`
  display: inline-block;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  background: var(--color-surface)fff;
  padding: 4px 16px;
  border: 2px solid var(--color-border);
  border-radius: 20px;
  margin-bottom: 8px;
  margin-top: 8px;

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const CoinModalInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
  }

  :root:not([data-theme='light']) & {
    background: var(--color-surface);
    color: var(--color-text-primary);
  }
`;

// 코인 양 입력 컨테이너 (알람 시간 입력 방식)
const CoinAmountContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 0;
`;

const CoinButton = styled.button`
  padding: 8px 12px;
  height: 40px;
  min-height: 40px;
  background: var(--color-surface)fff;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 24px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 40px;
  min-width: 40px;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CoinAmountDisplay = styled.div<{ $isCoinUse?: boolean }>`
  font-size: 2.25rem; /* 더 큰 텍스트 크기 */
  font-weight: bold;
  color: ${props => props.$isCoinUse ? '#ff6b6b' : 'var(--color-text-primary)'};
  text-align: center;
  min-width: 140px;
  padding: 4px 20px;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  user-select: none;
  
  &:hover {
    background-color: var(--color-panel);
  }
`;

const CoinAmountInput = styled.input<{ $isCoinUse?: boolean }>`
  font-size: 2.25rem; /* 더 큰 텍스트 크기 */
  font-weight: bold;
  color: ${props => props.$isCoinUse ? '#ff6b6b' : 'var(--color-text-primary)'};
  text-align: center;
  min-width: 140px;
  padding: 4px 20px;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  background: var(--color-surface);
  outline: none;
  
  &:focus {
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 3px rgba(var(--color-accent-rgb), 0.1);
  }
`;

// 페이지네이션 스타일 (루틴과제 날짜 블럭 위 페이지네이션 버튼 세트와 동일)
const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 8px 0;
  padding: 0;
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 18px;
  font-weight: 600;
  flex-shrink: 0;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  box-sizing: border-box;
`;

// 타임태그와 메모를 함께 표시하는 컨테이너
const MemoWithTimeTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

// 타임태그 배지
const TimeTagBadge = styled.span`
  background: var(--color-text-muted);
  color: white;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
`;


// 메모 텍스트
const MemoText = styled.span`
  flex: 1;
  min-width: 0;
`;

// 스토어 모달 스타일 (공통 StoreModal로 이동)

 

// 아이템 카드 사용 토스트 스타일
const UseCardToast = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  animation: useCardToastFadeIn 0.3s ease;
  
  @keyframes useCardToastFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const UseCardToastContent = styled.div`
  background: #ffffff !important;
  border-radius: 16px;
  padding: 24px 32px;
  box-shadow: var(--modal-shadow-large);
  text-align: center;
  min-width: 280px;
  width: 100%;
  max-width: 320px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const UseCardToastConfirmButton = styled.button`
  width: 100%;
  margin-top: 4px;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: var(--color-accent-strong);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: opacity 0.2s;
  box-sizing: border-box;

  &:hover {
    opacity: 0.9;
  }
  &:active {
    opacity: 0.85;
  }
`;

const UseCardToastImage = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 12px;
  background: var(--color-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(var(--color-accent-rgb), 0.3);
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UseCardToastMessageWrap = styled.div`
  border-radius: 12px;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid var(--color-block-border);
  display: inline-block;
`;

const UseCardToastMessage = styled.div`
  font-size: 18px;
  color: #000000 !important;
  font-weight: 700;
`;

const UseCardToastLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333;
`;

const UseCardToastRemaining = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  span:not(.remaining-text) {
    font-size: 16px;
    font-weight: 700;
    color: #e74c3c; /* 하트 아이콘과 같은 색상 */
  }
  
  .remaining-text {
    font-size: 14px;
    font-weight: 600;
    color: #e74c3c;
    margin-left: 4px;
  }
`;

// 액티브 카드 사용 모달 스타일
// 아이템 카드 삭제 모달 스타일
const DeleteCardModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
`;

const DeleteCardModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  width: 90%;
  max-width: 360px;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  z-index: 1101;
  overflow: hidden;
`;

const DeleteCardModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--color-surface)5f5;
  border-bottom: 1px solid #fecaca;
`;

const DeleteCardModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #dc2626;
`;

const DeleteCardModalCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-muted);
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--color-text-secondary);
  }
`;

const DeleteCardModalBody = styled.div`
  padding: 24px;
`;

const DeleteCardInfo = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const DeleteCardName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 12px;
`;

const DeleteCardMessage = styled.div`
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
`;

const DeleteCardWarning = styled.div`
  font-size: 13px;
  color: #dc2626;
  font-weight: 500;
`;

const DeleteCardModalButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const DeleteCardCancelButton = styled.button`
  flex: 1;
  padding: 12px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-panel);
  }
`;

const DeleteCardConfirmButton = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  background: #dc2626;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #b91c1c;
  }
  
  &:disabled {
    background: #fca5a5;
    cursor: not-allowed;
  }
`;

const UseCardModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1200;
`;

const UseCardModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  z-index: 1201;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const UseCardModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-panel);
`;

const UseCardModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const UseCardModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: var(--color-text-primary);
  }
`;

const UseCardModalContent = styled.div`
  padding: 20px;
  text-align: center;
  overflow-y: auto;
  flex: 1;
`;

const UseCardModalMessage = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.5;
  margin-bottom: 20px;
  word-break: keep-all;
`;

const UseCardHistorySection = styled.div`
  margin-bottom: 16px;
  text-align: left;
`;

const UseCardHistoryTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
`;

const UseCardHistoryList = styled.div`
  background: var(--color-accent-soft);
  border: 1px solid rgba(var(--color-accent-rgb), 0.2);
  border-radius: 8px;
  padding: 10px;
  max-height: 150px;
  overflow-y: auto;
`;

const UseCardHistoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(var(--color-accent-rgb), 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const UseCardHistoryDate = styled.span`
  font-size: 12px;
  color: var(--color-accent-text);
  font-weight: 500;
  white-space: nowrap;
`;

const UseCardHistoryMemo = styled.span`
  font-size: 12px;
  color: var(--color-text-gray);
`;

const UseCardInputSection = styled.div`
  text-align: left;
`;

const UseCardExhaustedMessage = styled.div`
  font-size: 14px;
  color: #6c757d;
  background: var(--color-panel);
  padding: 16px;
  border-radius: 8px;
  text-align: center;
`;

const UseCardMemoInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const UseCardMemoCounter = styled.div`
  text-align: right;
  font-size: 12px;
  color: #6c757d;
  margin-top: 6px;
`;

const UseCardModalFooter = styled.div`
  display: flex;
  border-top: 1px solid var(--color-border);
`;

const UseCardCancelButton = styled.button`
  flex: 1;
  padding: 16px;
  background: var(--color-panel);
  border: none;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: var(--color-border);
  }
`;

const UseCardConfirmButton = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 16px;
  background: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-accent-strong)'};
  border: none;
  font-size: 15px;
  font-weight: 600;
  color: white;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s ease;
  
  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.9);
  }
`;

